<script>
import 'regenerator-runtime/runtime';
import Axios from '../classes/Axios.js';
import DateField from './DateField.vue';
import {defineComponent, ref} from 'vue';
import autocomplete from '../components/autocomplete.vue';
import oasisModal from '../components/oasis-modal.vue';
import signaturePad from '../components/signaturePad.vue';
import Dropzone from 'vue2-dropzone';
import Dialog from '../components/Dialog.vue';
import PollingMixin from '../mixins/PollingMixin.js';
import IntlMixin from '../mixins/IntlMixin.js';
import UtilitiesMixin from '../mixins/UtilitiesMixin.js';
import UtilitiesStartMixin from '../mixins/UtilitiesStartMixin.js';
import DateFormatUtil from '../mixins/DateFormatUtil.js';
import InventoryMixin from '../mixins/InventoryMixin';
import { DateTime } from 'luxon';

export default defineComponent ({
    name: 'Approve',

    mixins: [PollingMixin, IntlMixin, UtilitiesMixin, UtilitiesStartMixin, DateFormatUtil, InventoryMixin],

    emits: ['onClose', 'onChanged', 'onNext', 'onPrevious', 'onApprove', 'onSave', 'kickback'],

    components: {
        autocomplete, 
        DateField,
        'oasis-modal': oasisModal,
        'signature-pad': signaturePad,
        Dropzone,
        Dialog,
    },

    props: {
        title: {
            type: String,
            default: 'Start Review',
            required: false,
        },
        entityId: {
            type: [String, Number],
            required: false,
        },
        adminAccess: {
            type: Boolean,
            required: false,
            default: false,
        },
        reference: {
            type: String,
            required: false,
            default: 'roster',
        },
        classes: {
            type: [ String, Array ],
            required: false,
            default: 'form-control'
        },
        assignment: {
            type: Object,
            required: true,
        },
        enableProBooks: {
            type: Boolean,
            required: false,
            default: false,
        },
        canApprove: {
            type: Boolean,
            required: true,
        },
        mainLocation: {
            type: Object,
            required: false,
            default: () => { country_id: 1 }
        },
        nextStart: {
            // type: Object,
        },
        prevStart: {
            // type: Object,
        },
    },

    data() {
        return {
            loading: false, // spinner
            iteration: 0,
            modified: false,
            // locale: window.navigator.userLanguage || window.navigator.language || 'en-US', // 'fr-fr' // test stuff
            oasisApi: new Axios('/restv2'),
            oasisV3Api: new Axios('/restv3'),
            currentEmployee: undefined,
            format: 'MM/dd/yyyy',
            type: 'employer',

            // Dialog
            approveModal: false,
            approvedThisSession: false,
            acknowledge: { 0: true },
            toast: false,
            toastMessage: '',
            toastClass: 'toast-success',
            user: user,
            employerSignature: undefined,

            readonlyI9: false,
            hasPrepWrap: false,
            selectedEntityType: '',
            everifyEligible: '',
            i9_verified: false,
            ignoreI9: false,
            customForms: [],
            departments: [],
            optionValues: [],
            COAObj: undefined,

            // Dropzone
            uploadedDocs: [],
            dzoptions: {
                headers: { "Auth": authToken },
                acceptedFileTypes: ".txt,.pdf,.jpeg,.jpg,.heic,.png,.tif,.tiff,.doc,.docx,.xls,.xlsx",
                maxFileSizeInMB: 12,
                uploadMultiple: true,
                maxNumberOfFiles: 5,
                showRemoveLink: false,
                thumbnailHeight: 20,
                thumbnailWidth: 20,
            },

            errors: {
                'tin': false,
                'foreign_tin': false,
                'birth_date': false,
                'fein': false,
                'list_a_type_id': false,
                'list_b_type_id': false,
                'list_c_type_id': false,
                'list_a_number': false,
                'list_b_number': false,
                'list_c_number': false,
                'list_a_exp': false,
                'list_b_exp': false,
                'list_c_exp': false,
                'ee_list_a_type_id': false,
                'ee_list_b_type_id': false,
                'ee_list_c_type_id': false,
                'ee_list_a_number': false,
                'ee_list_b_number': false,
                'ee_list_c_number': false,
                'ee_list_a_exp': false,
                'ee_list_b_exp': false,
                'ee_list_c_exp': false,
                'certification_completion_date': false,
                'password': false,
                'repeatPassword': false,
            },
            changed: {
            },
        }
    },

    computed: {
        employee() {
            if (!this.currentEmployee) {
                return {};
            } else {
                return this.currentEmployee;
            }
        },

        employer() {
            if (!this.user) {
                return {};
            } else {
                return this.user;
            }
        },

        localFormatBirthDate() {
            if (this.employee.birth_date) return DateTime.fromFormat(this.employee.birth_date, 'MM/dd/yyyy').toFormat(this.format);
        },

        ratePerUnit () {
            let hourly = parseInt(this.assignment.rate_unit_id) === 1 ? '' : ' (' + (this.hourlyRate) + ')';
            return this.assignment.rate + ' / ' + this.assignment.rate_unit + hourly;
        },

        hourlyRate () {
            return (this.assignment.hourly_rate) ? this.assignment.hourly_rate + ' / Hour' : '';
        },

        isUnion() {
            if (!this.assignment || !this.employee) return false;
            return parseInt(this.assignment.legislative_type_id) === 4;
        },

        isForeign() {
            if (!this.assignment || !this.employee) return false;
            return (parseInt(this.assignment.currency_id)) > 1 || (parseInt(this.employee.main_country_id)) > 1 ;
        },

        isCanada() {
            if (!this.assignment || !this.employee) return false;
            return (parseInt(this.assignment.currency_id)) === 2 || (parseInt(this.employee.main_country_id)) === 2 ;
        },

        isSsnNeeded() {
            if (!this.assignment || !this.employee) return false;
            return (parseInt(this.assignment.currency_id)) === 1 || (parseInt(this.employee.main_country_id)) === 1 || (this.employee.citizenship_id && ([ 17, 18, 19, 239 ].includes(parseInt(this.employee.citizenship_id))));
        },

        isW4Needed() {
            if (!this.assignment || !this.employee) return false;
            return (parseInt(this.assignment.currency_id)) === 1 || (parseInt(this.employee.main_country_id)) === 1 ;
        },

        isI9Needed() {
            if (!this.assignment || !this.employee) return false;
            return parseInt(this.assignment.currency_id) === 1 && parseInt(this.assignment.loanout_status) === 0;
        },

        isI9Importable() {
            if (!this.assignment || !this.employee) return false;
            return (!this.assignment.list_a_type_id && this.assignment.ee_list_a_type_id) || (!this.assignment.list_b_type_id && this.assignment.ee_list_b_type_id);
        },

        isI9Reviewable() {
            if (!this.assignment || !this.employee) return false;
            return (this.assignment.list_a_type_id && this.assignment.list_a_number) || (this.assignment.list_b_type_id && this.assignment.list_b_number);
        },

        isComplete() {
            if (!this.assignment || !this.employee) return false;
        },

        today() {
            let date = new Date();
            var day = date.getDate();
            var monthIndex = date.getMonth();
            var year = date.getFullYear();

            return year + '-' + this.pad(monthIndex * 1 + 1) + '-' + this.pad(day);
        },

        assignedForms() {
            if (!this.assignment.custom_forms) return [];
            else return typeof this.assignment.custom_forms === 'string' ? JSON.parse(this.assignment.custom_forms) : this.assignment.custom_forms;
        },

        customFormFields() {
            if (!this.assignment || !this.assignment.custom_forms) return [];
            const fields = typeof this.assignment.custom_form_fields === 'string' ? JSON.parse(this.assignment.custom_form_fields) : this.assignment.custom_form_fields;

            if (typeof fields !== 'object') return null;
            else if (Object.keys(fields).length === 0) return null;
            // else return Object.keys(fields);
            else return fields;
        },

        customFormEmployerFields() {
            if (!this.assignment || !this.assignment.custom_forms) return [];
            const fields = typeof this.assignment.custom_form_employer_fields === 'string' ? JSON.parse(this.assignment.custom_form_employer_fields) : this.assignment.custom_form_employer_fields;

            if (typeof fields !== 'object') return null;
            else if (Object.keys(fields).length === 0) return null;
            // else return Object.keys(fields);
            else return fields;
        },

        customDocumentsWithFormFields() {
            if (!this.assignment || !this.assignment.custom_forms) return [];
            const fields = typeof this.assignment.custom_form_fields === 'string' ? JSON.parse(this.assignment.custom_form_fields) : this.assignment.custom_form_fields;

            let docs = [];
            if (this.assignedForms.length < 1) return docs;
            else if (typeof fields !== 'object') return docs;
            else if (Object.keys(fields).length === 0) return docs;
            else {
                this.customDocuments.map((doc) => {

                })
            }
        },

        customEmployerForms() {
            return this.customForms.filter(({id, fields}) => {
                let formMatch = (this.assignedForms && Array.isArray(this.assignedForms) && (this.assignedForms || []).includes(id)) ? true : false;
                let matches = fields.find((field) => field.role === 'employer');
                if (formMatch && matches) return true;
            });
        },

        customEmployeeForms() {
            return this.customForms.filter(({id, fields}) => {
                let formMatch = (this.assignedForms && Array.isArray(this.assignedForms) && (this.assignedForms || []).includes(id)) ? true : false;
                let matches = fields.find((field) => field.role === 'employee');
                if (formMatch && matches) return true;
            });
        },

        probooksAccounts() {
            if (!this.COAObj) return [];
            return this.COAObj.accounts || [];
        },

        probooksFreeFields() {
            if (!this.COAObj) return [];
            return this.COAObj.freeFields || [];
        },

        probooksSeries() {
            if (!this.COAObj) return [];
            return this.COAObj.series || [];
        },

        probooksLocations() {
            if (!this.COAObj) return [];
            return this.COAObj.locations || [];
        },

        probooksSets() {
            if (!this.COAObj) return [];
            return this.COAObj.sets || [];
        },

        probooksProjects() {
            if (!this.COAObj) return [];
            return this.COAObj.projects || [];
        },

        loanoutStatesRegistered() {
            let states = this.employee.loanout_states_registered;
            if (! states) {
                return ''
            }
            if (typeof states === 'string' && this.isJson(states)) {
                let stateObj = JSON.parse(states);
                if (Array.isArray(stateObj)) {
                    return stateObj.map((lo) => lo.code).join(', ');
                } else {
                    return '';
                }
            } else {
                return '';
            }
        },

        optionValuesI9ListA() {
            if (!this.optionValues) return [];
            return this.optionValues.filter((o) => parseInt(o.option_type_id) === 7);
        },

        optionValuesI9ListB() {
            if (!this.optionValues) return [];
            return this.optionValues.filter((o) => parseInt(o.option_type_id) === 8);
        },

        optionValuesI9ListC() {
            if (!this.optionValues) return [];
            return this.optionValues.filter((o) => parseInt(o.option_type_id) === 9);
        },

        // Don't show DD account
        filteredDocs() {
            if (!this.uploadedDocs || this.uploadedDocs.length ===0) return [];
            const results = this.uploadedDocs.filter((doc) => true );
            const docs = this.uploadedDocs.sort((a, b) => new Date(b.time_added) - new Date(a.time_added));

            const result = [];
            for (const i in docs) {
                const doc = docs[i];
                if (!doc.id) {
                    continue;
                } else if (doc.type === 'voidedCheck') {
                    continue;
                // Only show the latest signed start packet
                } else if (doc.type === 'signedStart' && result.find((d) => d.type === 'signedStart')) {
                    continue;
                }

                // Make the type look better
                if (doc.type === 'signedStart') doc.typeNew = 'Signed Start';
                else if (doc.type === 'i9doc') doc.typeNew = 'I9 Doc';
                else if (doc.type === 'loanout') doc.typeNew = 'Loanout Doc';
                else if (doc.type === 'start') doc.typeNew = 'Employee Uploaded';
                else if (doc.type === 'agent') doc.typeNew = 'Agent Info';
                else if (doc.type === 'certdoc') doc.typeNew = 'Harassment Cert';
                else if (doc.type === 'boxdoc') doc.typeNew = 'Box/Kit Inventory';
                result.push(doc);
            }
            return result;
        },

        // Uploaded Assets
        loAssets() {
            return this.uploadedDocs.filter( ({ type }) => type === 'loanout');
        },

        i9Docs() {
            return this.uploadedDocs.filter( ({ type }) => type === 'i9doc');
        },

        ddDocs() {
            return this.uploadedDocs.filter( ({ type }) => type === 'voidedCheck');
        },

        certDocs() {
            return this.uploadedDocs.filter( ({ type }) => type === 'certdoc');
        },

        boxDocs() {
            return this.uploadedDocs.filter( ({ type }) => type === 'boxdoc');
        },

        // stateForms() {
        //     if (! stateForms || ! Array.isArray(stateForms)) return [];
        //     else return stateForms.filter((form) => parseInt(form.document_type_id || 0) === 99);
        // },

        // selectedStateForms() {
        //     if (! this.employee.state_forms || ! Array.isArray(this.employee.state_forms)) {
        //         return [];
        //     } else {
        //         const stateForms = this.stateForms.filter( ( { id } ) => this.employee.state_forms.map(formId => parseInt(formId)).includes(parseInt(id)) );
        //         const incentiveForms = this.incentiveForms.filter( ( { id } ) => this.employee.state_forms.map(formId => parseInt(formId)).includes(parseInt(id)) );
        //         return stateForms.concat(incentiveForms);
        //     }
        // },

        // incentiveForms() {
        //     if (! stateForms || ! Array.isArray(stateForms)) return [];
        //     else return stateForms.filter((form) => parseInt(form.document_type_id || 0) === 157);
        // },

        // incentiveStates() {
        //     if (! this.incentiveForms || ! Array.isArray(this.incentiveForms)) return [];
        //     const incentiveFormStates = [ ... new Set(this.incentiveForms.map((form) => parseInt(((form.properties || {}).locations || {}).defaultState || 0))) ];
        //     const intersection = this.allLocationStateIds.filter(state => incentiveFormStates.includes(state));
        //     const states = this.states.filter((state) => intersection.includes(parseInt(state.id)));
        //     return states;
        // },

        // matchingIncentiveForms() {
        //     if (! this.incentiveForms || ! Array.isArray(this.incentiveForms) || ! this.employee.metadata.residentState || typeof this.employee.metadata.residentState !== 'object') return [];
        //     const incentiveForms = [ ... new Set(this.incentiveForms.filter((form) => this.employee.metadata.residentState[parseInt(((form.properties || {}).locations || {}).defaultState || 0)])) ];
        //     return incentiveForms;
        // },
    },

    methods: {
        async loadEmployee(employeeId) {
            const eeId = employeeId || this.assignment.employee_id;
            if (!eeId) {
                return false;
            }

            const response = await this.oasisApi.get(`/employees/${eeId}`);
            this.currentEmployee = response.data
            this.getAssets();

            // I9 verified?
            if (this.assignment.i9_verifier_id && this.assignment.i9_verified_date) {
                this.readonlyI9 = true;
                this.i9_verified = true;
            } else {
                this.readonlyI9 = false;
                this.i9_verified = false;
            }

            // Load Kit Rental
            if ((this.assignment.kit_rental && this.assignment.kit_rental > 0) || (this.assignment.kit_rental_tax && this.assignment.kit_rental_tax > 0)) {
                this.loadKitInventory();
            }
            // this.employee = await this.oasisV3Api.get(`/employer/employees/${eeId}`);
            // console.log (this.currentEmployee, this.assignment.loanout_status, this.currentEmployee.special_type_properties, this.currentEmployee.special_type);
            setTimeout(() => { this.checkForm() }, 500);
        },

        approvalCheck() {
            if (this.modified) {
                this.showToast('Please save before approving.');
                return;
            }

            const test = this.checkForm();
            if (test === false) {
                this.showToast('Please note missing or correct data before approving.');
                return;
            }

            // Now check I9 if not already approved
            if (this.i9_verified && !this.assignment.i9_verifier_id) {
                const verify = this.verifyI9();
                if (verify.status === false) {
                    this.showToast('Please review the I9 information for errors');
                    return;
                }
            }

            // extra to certify I9
            if (!this.i9_verified && this.ignoreI9 === false) {
                this.dialogOpen('ignoreI9Dialog');
                return;
            }

            // Do not show the signature box again this session.
            if (this.approvedThisSession) {
                this.approveStart();
            } else {
                this.approveModal = true;
            }
        },

        approveWithoutI9() {
            this.ignoreI9=true;
            this.dialogClose('ignoreI9Dialog');

            // Do not show the signature box again this session.
            if (this.approvedThisSession) {
                this.approveStart();
            } else {
                this.approveModal = true;
            }
        },

        async approveStart() {
            let message;
            this.approveModal = false
            this.approvedThisSession = true;
            const advance = false; // Since we are now removing the record, no need to advance the row counter

            // Save and approve
            const options = { approve: true };
            try {
                const response = await this.patchAssignment(options);
                if (response.status === 200 || response.status === 201) {
                    message = `Start Approved for ${this.employee.last_name}, ${this.employee.first_name}`;
                    if (this.nextStart) {
                        message += `<br />Now viewing the next start`;
                    }
                    this.$emit('onApprove', this.assignment.id)
                    this.gotoNextStart(advance);
                } else {
                    message = `Error Approving for ${this.employee.last_name}, ${this.employee.first_name}`;
                }
            } catch (error) {
                console.log(error);
                message = `Error Approving for ${this.employee.last_name}, ${this.employee.first_name}`;
            } finally {
                this.loading = false;
            }
            if (message)this.showToast(message);
        },

        clearErrors() {
            for (const ref in this.$refs) {
                let element = this.$refs[ref];

                if (Array.isArray(element)) {
                    element = element[0];
                }
                if (typeof element === 'undefined' || element === undefined || !element || !element.classList) {
                    continue;
                }
                // If custom field, highlight description, too
                let descElement;
                if (ref.match(/state_form_fields/)) {
                    descElement = this.$refs[`description.${ref}`];
                } else if (ref.match(/custom_form_fields/)) {
                    descElement = this.$refs['description.' + ref];
                } else if (ref.match(/canada_form_fields/)) {
                    descElement = this.$refs['description.' + ref];
                }

                if (descElement && Array.isArray(descElement)) {
                    descElement = descElement[0];
                } else if (typeof descElement === 'undefined' || descElement === undefined) {
                    descElement = false;
                }

                element.classList.remove('form-error');  // Remove these errors as they are returned from API
                element.classList.remove('form-error2');  // Remove these errors as they are returned from API
                element.classList.remove('form-missing');  // Remove these errors as they are returned from API
            }
        },

        // Easy validation, e.g. if fields are required
        checkForm(form) {
            // we don't need to run this function to employee. we have another validate function for employee
            let errors = false;
            //  we need to add this code in checkform function to check whether I9 date field is error or not.
            if ( this.errors.list_a_exp || this.errors.list_b_exp || this.errors.list_c_exp ) errors = true;
            for (const ref in this.$refs) {
                let element = this.$refs[ref];

                if (Array.isArray(element)) {
                    element = element[0];
                }
                if (typeof element === 'undefined' || element === undefined || !element) {
                    continue;
                }

                // Handle our custom elements
                if (!element.classList && element.isError) {
                    console.log(element, element.name);
                    errors = true;
                } else if (element.classList) {
                    // If custom field, highlight description, too
                    let descElement;
                    if (ref.match(/state_form_fields/)) {
                        descElement = this.$refs[`description.${ref}`];
                    } else if (ref.match(/custom_form_fields/)) {
                        descElement = this.$refs['description.' + ref];
                    } else if (ref.match(/canada_form_fields/)) {
                        descElement = this.$refs['description.' + ref];
                    }

                    if (descElement && Array.isArray(descElement)) {
                        descElement = descElement[0];
                    } else if (typeof descElement === 'undefined' || descElement === undefined) {
                        descElement = false;
                    }

                    element.classList.remove('form-error2');  // Remove these errors as they are returned from API
                    if (!element.required) {
                        continue;
                    }

                    if (element.classList.contains('form-error')) {
                        errors = true;
                        // console.log(element.name)
                        if (descElement) descElement.classList.add('form-error');
                    } else if (element.type.toLowerCase() === 'checkbox' && element.checked === false) {
                        errors = true;
                        element.classList.add('form-missing');
                        if (descElement) descElement.classList.add('form-missing');
                    } else if (element.type.toLowerCase() === 'radio' && this.$refs[element.name] && Array.isArray(this.$refs[element.name]) && !this.$refs[element.name].find((el) => el.checked === true)) {
                        errors = true;
                        element.classList.add('form-missing');
                        if (descElement) descElement.classList.add('form-missing');
                    } else if (element.value === '' || element.value === null) {
                        errors = true;
                        element.classList.add('form-missing');
                        // console.log(element.name)
                    } else {
                        element.classList.remove('form-missing');
                        if (descElement) descElement.classList.remove('form-missing');
                    }
                } else {
                    continue;
                }
            }
            // check component address data that are required. not working $refs in parent as normal.
            let main_address1 = document.getElementsByName("main_address1");
            let loanout_address1 = document.getElementsByName("loanout_address1");
            let agent_address1 = document.getElementsByName("agency_address1");

            if (main_address1.length > 0 && main_address1[0].required && !this.employee.main_address1) {
                errors = true;
                main_address1[0].classList.add('form-missing');
            }

            if (loanout_address1.length > 0 && loanout_address1[0].required && !this.employee.loanout_address1) {
                errors = true;
                loanout_address1[0].classList.add('form-missing');
            }

            if (agent_address1.length > 0 && agent_address1[0].required && !this.employee.special_type_properties.agency_address1) {
                errors = true;
                agent_address1[0].classList.add('form-missing');
            }

            if (errors === true) {
                this.showToast('Please check highlighted fields before submitting.');
                return false;
            } else {
                return true;
            }
        },

        closeBox() {
            this.$emit('onClose');
            this.clearErrors();
            this.reset();
        },

        gotoNextStart(advance=true) {
            if (this.modified) {
                this.dialogOpen('saveDialog');
                return;
            }
            this.$emit('onNext', advance);
            this.clearErrors();
            this.reset();
        },

        gotoPrevStart() {
            if (this.modified) {
                this.dialogOpen('saveDialog');
                return;
            }
            this.$emit('onPrevious');
            this.clearErrors();
            this.reset();
        },

        updateSignature(value){
            if (value) this.employerSignature = value;
            return true;
        },

        logChanges(event, changedValue=null) {
            if (! event || !event.target) return;
            // Don't log disabled fields
            if (event.target.readOnly || event.target.disabled) return;

            const field = event.target.name;
            const value = changedValue || event.target.value
            // const type = event.target.nodeName;
            // const selectedValue = type === 'SELECT' ? event.target.options[event.target.selectedIndex].innerHTML : null;

            // Compare to our assignment and employee fields
            const fieldsToSkip = [ 'id', 'hash', 'time_updated', 'time_added', 'status', 'employer_status', 'employee_status', 'modified' ];
            if (fieldsToSkip.includes(field)) return;

            if (this.assignment.hasOwnProperty(field) && !this.assignment[field] && !this.changed[field] && !value) {
                // No data.  Don't update
            } else if (this.assignment.hasOwnProperty(field) && (this.assignment[field] != value || this.changed[field] != value)) {
                // console.log(`Changed ${field} from ${this.changed[field]} to ${value} orig: ${this.assignment[field]}`)
                this.changed[field] = value;
                this.modified = true;
            } else if (this.employee.hasOwnProperty(field) && (this.employee[field] != value || this.changed[field] != value)) {
                // console.log(`Changed ${field} from ${this.changed[field]} to ${value} orig: ${this.assignment[field]}`)
                this.changed[field] = value;
                this.modified = true;
            } else {
                // console.log(`NO CHANGE ${field} from "${this.changed[field]}" to "${value}" orig: "${this.assignment[field]}"`)
            }

            this.updateStyles();
            return;
        },

        updateStyles() {
            // add style
            for (const field in this.changed) {
                const refField = this.$refs[`assignment.${field}`] || this.$refs[`employee.${field}`]
                if (refField && refField.classList) {
                    if (!refField.classList.contains('form-error') && !refField.classList.contains('form-error2')) refField.classList.add('form-change');
                } else {
                    // debugger
                }
            }
            return;
        },

        fringeCheck(selectedData) {
            // if we got error case, this function will not be fired.
            let val = ''
            if (selectedData && selectedData.code) {
                val = selectedData.code;
                const setFringe = selectedData.code.substring(0,selectedData.code.length-2) + '99';
                let validate = this.probooksAccounts.findIndex((el) => { return el.code == setFringe });
    
                if (validate == -1) {
                    this.assignment.fringe_code = '';
                    this.selected_fringe_code = '';
                } else if (selectedData.code.length < 4) {
                    this.assignment.fringe_code = '';
                    this.selected_fringe_code = '';
                } else {
                    this.assignment.fringe_code = setFringe;
                    this.selected_fringe_code = setFringe;
                }
            } else if (selectedData) {
                // this case would be custom data instead of selection. (if we set mustMatch = false, we will get this case)
                val = selectedData;
            }

            this.modified = true;
            this.assignment.budget_code = val;

            return;
        },

        setGlCode(event, type) {
            if (!event || !type) return;
            // console.log(event, type);
        },

        // Set modified and tracked changes
        reset() {
            this.modified = false;
            this.changed = {};
            this.uploadedDocs = [];
            this.ignoreI9 = false;
            for (const i in this.errors) {
                this.errors[i] = false;
            }
            for (const i in this.$refs) {
                if (!this.$refs[i]) continue;
                const classes = this.$refs[i].classList;
                if (classes) {
                    classes.remove('form-change');
                    classes.remove('form-error');
                    classes.remove('form-error2');
                }
            }
        },

        async save() {
            const test = this.checkForm();
            if (test === false) {
                this.showToast('Please note missing or correct data before approving.');
                return;
            }

            let message;
            try {
                const response = await this.patchAssignment(this.assignment.id, this.changed);
                if (response && (response.status === 200 || response.status === 201)) {
                    message = `Start Saved for ${this.employee.last_name}, ${this.employee.first_name}`;
                    this.$emit('onSave', this.assignment.id, this.changed);
                    this.reset();
                } else {
                    message = `Error Saving for ${this.employee.last_name}, ${this.employee.first_name}`;
                }
            } catch (error) {
                console.log(error);
                message = `Error Saving for ${this.employee.last_name}, ${this.employee.first_name}`;
            } finally {
                this.loading = false;
            }
            if (message)this.showToast(message);

            return;
        },

        async patchAssignment(options=null) {
            if (!this.assignment.id) {
                return false;
            }

            this.loading = true;
            const endpoint = '/assignments/' + this.assignment.id;
            let patchData = {}, assignmentPatchData = {}, i9PatchData = {}, certPatchData = {}, customPatchData = {}, customEmployerPatchData = {}, agentPatchData = {}, ddPatchData = {}, kitPatchData = {}, canadaPatchData = {};
            
            assignmentPatchData = this.changed;
            // $("#employeeForm .form-change").each(function(ind,el) { patchData[el.name] = el.value });

            // Add loanout states registered
            if (this.assignment.hasOwnProperty('loanout_status') && this.assignment.loanout_status && this.isJson(this.employee.loanout_states_registered)) {
                patchData.loanout_states_registered = (this.employee.loanout_states_registered);
                // patchData.loanout_states_registered = JSON.stringify(this.employee.loanout_states_registered);
            }

            if (patchData.birth_date) {
                let tempDate = DateTime.fromFormat(this.localFormatBirthDate, this.format).toFormat('MM/dd/yyyy');
                patchData.birth_date = tempDate;
            }

            // $("#customForm .form-change").each(function(ind,el) { customPatchData[el.name] = el.value });
            customPatchData = this.assignment.custom_form_fields;
            canadaPatchData = this.assignment.canada_form_fields;
            customEmployerPatchData = this.assignment.custom_form_employer_fields;
            kitPatchData = this.kitInventory || null;

            // Add dues
            // assignmentPatchData.union_dues_withholding_id = (this.$refs['assignment.union_dues_withholding_id'] || {}).value || this.assignment.union_dues_withholding_id || '';
            // assignmentPatchData.union_member_id = (this.$refs['assignment.union_member_id'] || {}).value || this.assignment.union_member_id || '';
            // assignmentPatchData.loanout_status = (this.$refs['assignment.loanout_status'] || {}).value || this.assignment.loanout_status || '0';
            // assignmentPatchData.metadata = this.assignment.metadata || {};
            // patchData.metadata = this.employee.metadata || {};

            // $("#i9Form .form-change").each(function(ind,el) { i9PatchData[el.name] = el.value });
            // $("#ddForm .form-change").each(function(ind,el) { ddPatchData[el.name] = el.value });
            // $("#certForm .form-change").each(function(ind,el) { certPatchData[el.name] = el.value });

            if (this.employee.special_type == 1) agentPatchData = this.employee.special_type_properties;
            if (this.employee.special_type == 0 && this.assignment.agent_fee) {
                this.assignment.agent_fee = '';
                assignmentPatchData.agent_fee = '';
            }

            // Only add I9 data if the checkbox to certify is checked, and I9 was not previously verified.
            if (this.i9_verified && !(this.assignment.i9_verifier_id && this.assignment.i9_verified_date)) {
                i9PatchData.i9_verifier_id = this.assignment.i9_verifier_id;
                i9PatchData.is_rehire = this.assignment.is_rehire;
            }
            // patchData.special_type = this.employee.special_type ? this.employee.special_type : 0;
            // patchData.papercheck_optin = this.employee.papercheck_optin > 0 || this.employee.papercheck_optin === true ? 1 : 0;

            // Update employer signature
            const employerData = this.employer.id && this.employerSignature ? { id: this.user.id, signature: this.employerSignature } : {};

            let data = {
                employeeId: this.employee.id,
                employee: patchData,
                employeefull: this.employee,

                assignmentId: this.assignment.id,
                assignment: assignmentPatchData,
                i9Data: i9PatchData,
                certData: certPatchData,
                agentData: agentPatchData,
                ddData: ddPatchData,
                customForm: customPatchData,
                customEmployerForm: customEmployerPatchData,
                canadaForm: canadaPatchData,
                kitData: kitPatchData,
                assignmentfull: this.assignment,
                employer: employerData,
                changes: this.changes
            };

            if (options && options.approve === true) {
                // console.log ('Approve');
                data.approve = true;
            } else {
                data.approve = false;
            }
            // console.log(data);

            let response;
            try {
                response = await this.oasisApi.patch(endpoint, data);
                if (response && (response.status === 200 || response.status === 202) && response.data) {
                    // return it later
                } else if (response && response.status === 400) {
                    if (response.data.message) {
                        this.showToast(response.data.message);
                    } else {
                        this.showToast(response.data);
                    }
                } else {
                    console.error(response.data || response);
                }
            } catch (error) {
                console.error(error);
            } finally {
                this.loading = false;
            }
            return response
        },

        openLink(type) {
            if (!type || !this.assignment) {
                alert('No employee data');
                return
            }

            const href = type === 'employee' ? `/oasis/starts/approve/${this.assignment.hash}` : `/oasis/starts/edit/${this.assignment.hash}`;
            window.open(href, '_start').focus();
        },

        setNoExpDate(field) {
            this.assignment[field] = 'NA';
            const refField = this.$refs[`assignment.${field}`] || this.$refs[`employee.${field}`]
            if (refField && refField.classList) {
                refField.classList.remove('form-error');
                refField.classList.remove('form-error2');
                this.logChanges({target: refField}, this.assignment[field]);
            }
            // this.changedValue(field);
        },

        // Copies employee-entered I9 data into employer fields to comply with DHS rules
        importEeI9() {
            const i9Fields = [ 'list_a_type', 'list_a_type_id', 'list_a_number', 'list_a_auth', 'list_a_exp', 'list_b_type', 'list_b_type_id', 'list_b_number', 'list_b_auth', 'list_b_exp', 'list_c_type', 'list_c_type_id', 'list_c_number', 'list_c_auth', 'list_c_exp' ];

            i9Fields.forEach((employerField) => {
                const employeeField = 'ee_' + employerField;
                if (this.assignment[employeeField]) {
                    this.readonlyI9 = false;
                    this.assignment[employerField] = this.assignment[employeeField];
                    // this.changed[employerField] = this.assignment[employeeField];
                    const refField = this.$refs[`assignment.${employerField}`] || this.$refs[`employee.${employerField}`]
                    this.logChanges({target: refField}, this.assignment[employeeField]);
                    // we changed to use date-field component instead of using html input tag directly. we got css error here. so I changed the way to manage css I9 date field.
                    if (employerField == 'list_a_exp' || employerField == 'list_b_exp' || employerField == 'list_c_exp'){
                        this.errors[employerField] = false;
                    } else {
                        // this.highlightChangeI9({ target: this.$refs['assignment.' + employerField] });
                    }
                }
            });
        },

        showToast(message) {
            this.toastMessage = message;
            this.toast = true;
            setTimeout(() => {
                this.message = '';
                this.toast = false;
            }, 1000 * 3);
        },

        dialogOpen(ref=null) {
            const dialog = this.$refs[ref];
            if (dialog) dialog.openDialog();
        },

        dialogClose(ref=null) {
            const dialog = this.$refs[ref];
            if (dialog) dialog.closeDialog();
        },

        dialogDiscard(ref=null, direction='next') {
            const dialog = this.$refs[ref];
            if (dialog) {
                this.dialogClose(ref);
                this.modified = false;
                if (direction === 'next' && this.nextStart) {
                    this.gotoNextStart();
                }
                if (direction === 'prev' && this.prevStart) {
                    this.gotoPrevStart();
                }
            }
        },

        async dialogSave(ref=null, direction='next') {
            const dialog = this.$refs[ref];
            if (dialog) {
                this.dialogClose(ref);
                await this.save();
                if (direction === 'next' && this.nextStart) {
                    this.gotoNextStart();
                }
                if (direction === 'prev' && this.prevStart) {
                    this.gotoPrevStart();
                }
            }
        },

        async getAssets() {
            if (!this.assignment) return false;
            this.loading = true;
            let data = {
                hash: this.assignment.hash,
                assignmentId: this.assignment.id,
                employeeId: this.assignment.employee_id
            };

            try {
                const response = await this.oasisApi.post('/assets/get/starts', data)
                if (response.status == 200 ) {
                    this.uploadedDocs = response.data;
                }
            } catch (error) {
                console.log('error: '); console.log(error); 
            } finally {
                this.loading = false;
            }
        },

        dzSuccess() {
            this.getAssets();
        },
    },

    watch: {
        assignment(newValue, oldValue) {
            if (newValue) this.loadEmployee();
            this.assignment.krtax_free_field2 = newValue.krtax_free_field2;
            this.iteration++;
        },

        enableProBooks(newValue) {
            if (newValue) this.COAObj = JSON.parse(window.localStorage.getItem(entityId + '#'));
        },
    },

    async created() {},

    async mounted() {
        setTimeout(() => { $('[data-toggle="tooltip"]').tooltip() }, 500);

        // Prevent back button from failing.
        history.pushState(null, document.referrer, location.href);
        window.onpopstate = function () {
            window.location.href = document.referrer;
        };

        this.loading = true;

        // Load form data
        const depts = await this.oasisApi.get('/departments');
        if (depts && depts.status == '200' && depts.data && depts.data.results) {
            this.departments = depts.data.results;
        }

        const optionValues = await this.oasisV3Api.get('/optionValues?status=1')
        if (optionValues && optionValues.status == '200' && optionValues.data && optionValues.data.results) {
            this.optionValues = optionValues.data.results;
        }

        const forms = await this.oasisApi.get('/customDocuments');
        if (forms && forms.status == '200' && forms.data && forms.data.results) {
            this.customForms = forms.data.results;
        }

        await this.loadEmployee();
        if (this.enableProBooks) {
            this.COAObj = JSON.parse(window.localStorage.getItem(entityId + '#'));
        }

        this.loading = false;
        // this.getAvailableForms();
        // this.buildRowData();
    }
});
</script>

<template>
    <span v-if="toast" :class="toastClass" v-html="toastMessage"></span>
    <div class="reviewBox" @focusout="logChanges">
        <div>
            <a title="Close" class="close" @click="closeBox">X</a>
            <h4>Start Review</h4>
            <hr />
        </div>

        <div class="m-top-10 panel panel-warning" id="job1">
            <div class="panel-heading centered">
                <div class="panel-heading centered">
                    <div v-if="prevStart" @click="gotoPrevStart" class="left m-top-m5" >
                        <span id="chevron-arrow-left"></span> Previous
                    </div>
                    <div class="right m-top-m5">
                        <span class="p-right-5"><div v-if="loading" class="inline-loader" style="display:inline-block;vertical-align:middle;"></div></span>
                        <button class="btn m-top-m5 m-right-10" :class="modified ? 'btn-primary' : 'btn-default'" @click="save" :disabled="!modified">Save</button>
                        <button class="btn m-top-m5 m-right-10" :class="canApprove ? 'btn-success' : 'btn-default'" @click="approvalCheck" :disabled="!canApprove">Approve</button>
                        <span v-if="nextStart" @click="gotoNextStart">
                            Next<span id="chevron-arrow-right"></span>
                        </span>
                    </div>
                    <h4 style="margin: 0 auto; text-align: center; line-height: 0.5em;"><span class="bold">{{ `${employee.last_name}, ${employee.first_name}` + (assignment.loanout_status > 0 ? ` (${employee.loanout_name || 'TBD'})` : '') }}</span></h4>
                </div>
            </div>
            <div class="panel-body">
                <div class="col-sm-6">
                    <div class="hidden"><input class="form-control" type="text" id="assignmentId" name="assignmentId" v-model="assignment.id" ref="assignment.id" readonly /></div>
                    <div class="form-group">
                        <div class="col-sm-3"><label for="entity">Project</label></div>
                        <div class="col-sm-9"><input class="form-control" type="text" name="entity" v-model="assignment.entity" placeholder="" tabindex="-1" readonly /></div>
                        <div class="col-sm-3"><label for="location">Main Location</label></div>
                        <div class="col-sm-9"><input class="form-control" type="text" name="location" v-model="assignment.location" placeholder="" tabindex="-1" readonly /></div>
                        <div class="col-sm-3"><label for="start_date">Hire Date</label></div>
                        <div class="col-sm-3">
                            <!-- <input class="form-control no-date-widget" type="text" id="start_date" name="start_date" v-model="assignment.start_date" tabindex="-1" readonly /> -->
                            <date-field
                                name="start_date"
                                v-model="assignment.start_date"
                                reference="assignment.start_date"
                                ref="assignment.start_date"
                                :format="format"
                                :allowEmpty="true"
                                :placeholder="format.toUpperCase()"
                                :disabled="true"
                            />
                            </div>
                        <div class="col-sm-3"><label for="term_date">Term Date</label></div>

                        <div class="col-sm-3">
                            <date-field
                                name="term_date"
                                v-model="assignment.term_date"
                                reference="assignment.term_date"
                                ref="assignment.term_date"
                                :format="format"
                                :allowEmpty="true"
                                :placeholder="format.toUpperCase()"
                                :classes="[ 'form-control', errors.term_date === true ? 'form-error' : '', changed.term_date ? 'form-change' : '' ]"
                            />
                        </div>
 
                        <div class="col-sm-3"><label for="job_title">Job Title/Occ</label></div>
                        <div class="col-sm-6"><input class="form-control" name="job_title" v-model="assignment.job_title_override" title="" tabindex="-1" readonly /></div>
                        <label class="hidden" for="legislative_type">FLSA Status</label>
                        <div class="col-sm-3">
                            <input class="form-control" name="legislative_type" v-model="assignment.legislative_type" title="" tabindex="-1" readonly />
                        </div>
                        <div v-if="isUnion">
                            <div class="col-sm-3"><label for="job_title_override">Union/Occ</label></div>
                            <div class="col-sm-5"><input class="form-control" type="text" name="union_local" v-model="assignment.union_local" placeholder="" tabindex="-1" readonly /></div>
                            <div class="col-sm-4"><input class="form-control" type="text" name="department" v-model="assignment.occ_code" placeholder="" tabindex="-1" readonly /></div>
                        </div>
                        <div class="form-group">
                            <div class="col-sm-3"><label for="department_id">Dept</label></div>
                            <div class="col-sm-7">
                                <select class="form-control" name="department_id" v-model="assignment.department_id" ref="assignment.department_id" @change="departmentCheck" @select="departmentCheck">
                                    <option v-for="department in departments" :value="department.id">
                                        {{department.name}}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div v-if="assignment.rate_unit_id != 1 && assignment.hourly_rate">
                            <div class="col-sm-3"><label for="rate">Pay Rate</label></div>
                            <div class="col-sm-5"><input class="form-control" type="text" name="rate" v-model="ratePerUnit" tabindex="-1" readonly /></div>
                            <div class="col-sm-4">
                                <input class="form-control" type="text" name="hourly_rate" v-model="hourlyRate" tabindex="-1" readonly />
                            </div>
                        </div>
                        <div v-else>
                            <div class="col-sm-3"><label for="rate">Pay Rate per</label></div>
                            <label class="hidden" for="rate_unit_id">Pay Unit</label>
                            <div class="col-sm-5"><input class="form-control" type="text" name="rate" v-model="assignment.rate" tabindex="-1" readonly /></div>
                            <div class="col-sm-4">
                                <div v-if="assignment.rate_comment">
                                    <input class="form-control" type="text" name="rate_unit" v-model="assignment.rate_comment" tabindex="-1" readonly />
                                </div>
                                <div v-else>
                                    <input class="form-control" type="text" name="rate_unit" v-model="assignment.rate_unit" tabindex="-1" readonly />
                                </div>
                            </div>
                        </div>
                        <div class="clear"></div>
                        <div class="col-sm-3"><label for="guaranteed_hours"><span v-if="assignment.union_local_id">Guar. Hours</span><span v-else>Expected Hours</span></label></div>
                        <div class="col-sm-5"><input class="form-control" type="text" name="guaranteed_hours" v-model="assignment.guaranteed_hours" tabindex="-1" readonly /></div>
                        <div class="col-sm-4">
                            <input class="form-control" type="text" name="guaranteed_hours_unit" v-model="assignment.guaranteed_hours_unit" tabindex="-1" readonly />
                        </div>
                        <div class="col-sm-3"><label for="hourly_rate">Effective Hourly Rate</label></div>
                        <div class="col-sm-3"><input class="form-control" type="text" name="hourly_rate" v-model="assignment.hourly_rate" ref="assignment.hourly_rate" tabindex="-1" readonly /></div>
                        <div v-if="isUnion">
                            <div class="col-sm-3"><label for="scale_rate">Scale</label></div>
                            <div class="col-sm-3"><input class="form-control" type="text" name="scale_rate" v-model="assignment.scale_rate" ref="assignment.scale_rate" tabindex="-1" readonly /></div>
                        </div>
                        <div class="clear"></div>
                        <div v-if="enableProBooks">
                            <div class="col-sm-3"><label for="account_code">Shoot Acct</label></div>
                            <div class="col-sm-3">
                                <autocomplete :key="iteration" :options="probooksAccounts" name="budget_code" v-model="assignment.budget_code" ref="assignment.budget_code" :preselect="assignment.budget_code" :display="'code'" @select="fringeCheck" placeholder="Acct" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option" >
                                        <p><strong>{{ option.code + ' - ' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-3">
                                <autocomplete :key="iteration" :options="probooksAccounts" name="fringe_code" v-model="assignment.fringe_code" ref="assignment.fringe_code" :preselect="assignment.fringe_code" :display="'code'" @select="setGlCode('fringe_code')" placeholder="Fringe" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option" >
                                        <p><strong>{{ option.code + ' - ' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-3 timecard">
                                <div class="col-sm-6">
                                    <autocomplete :key="iteration" :options="probooksFreeFields" name="free_field" v-model="assignment.free_field" ref="assignment.free_field" :preselect="assignment.free_field" :display="'code'" @select="setGlCode('free_field')" placeholder="FF1" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                        <template v-slot:item="option" >
                                            <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                        </template>
                                    </autocomplete>
                                </div>
                                <div class="col-sm-6">
                                    <autocomplete :key="iteration" :options="probooksFreeFields" name="free_field2" v-model="assignment.free_field2" ref="assignment.free_field2" :preselect="assignment.free_field2" :display="'code'" @select="setGlCode('free_field2')" placeholder="FF2" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                        <template v-slot:item="option" >
                                            <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                        </template>
                                    </autocomplete>
                                </div>
                            </div>
                            <div class="clear"></div>
                            <div class="col-sm-3"><label for="account_code"></label></div>
                            <div class="col-sm-3">
                                <autocomplete :key="iteration" :options="probooksSeries" name="series_code" v-model="assignment.series_code" ref="assignment.series_code" :preselect="assignment.series_code" :display="'code'" @select="setGlCode('series')" placeholder="Series" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option" >
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-3">
                                <autocomplete :key="iteration" :options="probooksLocations" name="location_code" v-model="assignment.location_code" ref="assignment.location_code" :preselect="assignment.location_code" :display="'code'" @select="setGlCode('Location')" placeholder="Location" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option" >
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-3">
                                <autocomplete :key="iteration" :options="probooksSets" name="set_code" v-model="assignment.set_code" ref="assignment.set_code" :preselect="assignment.set_code" :display="'code'" @select="setGlCode('Set')" placeholder="Set" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option" >
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>

                            <div v-if="hasPrepWrap">
                                <div class="col-sm-3"><label for="account_code">Prep/Wrap Acct</label></div>
                                <div class="col-sm-3">
                                    <autocomplete :key="iteration" :options="probooksAccounts" name="pw_budget_code" v-model="assignment.pw_budget_code" :preselect="assignment.pw_budget_code" ref="assignment.pw_budget_code" @select="pwFringeCheck" :display="'code'"  placeholder="Acct" :default-show="true" :highlight="-1" :display-changes="true">
                                        <template v-slot:item="option">
                                            <p><strong>{{ option.code + ' - ' + option.itemname }}</strong></p>
                                        </template>
                                    </autocomplete>
                                </div>
                                <div class="col-sm-3">
                                    <autocomplete :key="iteration" :options="probooksAccounts" name="pw_fringe_code" v-model="assignment.pw_fringe_code" ref="assignment.pw_fringe_code" :preselect="assignment.pw_fringe_code" :display="'code'" @select="setGlCode('PWFringe')" placeholder="Fringe" :default-show="true" :highlight="-1" :display-changes="true">
                                        <template v-slot:item="option">
                                            <p><strong>{{ option.code + ' - ' + option.itemname }}</strong></p>
                                        </template>
                                    </autocomplete>
                                </div>
                                <div class="col-sm-3 timecard">
                                    <div class="col-sm-6">
                                        <autocomplete :key="iteration" :options="probooksFreeFields" name="pw_free_field" v-model="assignment.pw_free_field" ref="assignment.pw_free_field" :preselect="assignment.pw_free_field" :display="'code'" @select="setGlCode('pw_free_field')" placeholder="FF1" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                            <template v-slot:item="option">
                                                <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                            </template>
                                        </autocomplete>
                                    </div>
                                    <div class="col-sm-6">
                                        <autocomplete :key="iteration" :options="probooksFreeFields" name="pw_free_field2" v-model="assignment.pw_free_field2" ref="assignment.pw_free_field2" :preselect="assignment.pw_free_field2" :display="'code'" @select="setGlCode('pw_free_field2')" placeholder="FF2" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                            <template v-slot:item="option">
                                                <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                            </template>
                                        </autocomplete>
                                    </div>
                                </div>
                                <div class="clear"></div>
                                <div class="col-sm-3"><label for="account_code"></label></div>
                                <div class="col-sm-3">
                                    <autocomplete :key="iteration" :options="probooksSeries" name="pw_series_code" v-model="assignment.pw_series_code" ref="assignment.pw_series_code" :display="'code'" :preselect="assignment.pw_series_code" @select="setGlCode('PWSeries')" placeholder="Series" :default-show="true" :highlight="-1" :display-changes="true">
                                        <template v-slot:item="option">
                                            <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                        </template>
                                    </autocomplete>
                                </div>
                                <div class="col-sm-3">
                                    <autocomplete :key="iteration" :options="probooksLocations" name="pw_location_code" v-model="assignment.pw_location_code" ref="assignment.pw_location_code" :display="'code'" :preselect="assignment.pw_location_code" @select="setGlCode('PWLocation')" placeholder="Location" :default-show="true" :highlight="-1" :display-changes="true">
                                        <template v-slot:item="option">
                                            <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                        </template>
                                    </autocomplete>
                                </div>
                                <div class="col-sm-3">
                                    <autocomplete :key="iteration" :options="probooksSets" name="pw_set_code" v-model="assignment.pw_set_code" ref="assignment.pw_set_code" :display="'code'" :preselect="assignment.pw_set_code" @select="setGlCode('PWSet')" placeholder="Set" :default-show="true" :highlight="-1" :display-changes="true">
                                        <template v-slot:item="option">
                                            <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                        </template>
                                    </autocomplete>
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <div class="col-sm-3"><label for="account_code">Shoot Acct</label></div>
                            <div class="col-sm-3"><input class="form-control" type="text" name="budget_code" v-model="assignment.budget_code" ref="assignment.budget_code" placeholder="Acct" /></div>
                            <div class="col-sm-3"><input class="form-control" type="text" name="fringe_code" v-model="assignment.fringe_code" ref="assignment.fringe_code" placeholder="Fringe" /></div>
                            <div class="col-sm-3 timecard">
                                <div class="col-sm-6"><input class="form-control" type="text" name="free_field" v-model="assignment.free_field" ref="assignment.free_field" placeholder="FF1" /></div>
                                <div class="col-sm-6"><input class="form-control" type="text" name="free_field2" v-model="assignment.free_field2" ref="assignment.free_field2" placeholder="FF2" /></div>
                            </div>
                            <div class="clear"></div>
                            <div class="col-sm-3"><label for="account_code"></label></div>
                            <div class="col-sm-3"><input class="form-control" type="text" name="series_code" v-model="assignment.series_code" ref="assignment.series_code" placeholder="Series" /></div>
                            <div class="col-sm-3"><input class="form-control" type="text" name="location_code" v-model="assignment.location_code" ref="assignment.location_code" placeholder="Location" /></div>
                            <div class="col-sm-3"><input class="form-control" type="text" name="set_code" v-model="assignment.set_code" ref="assignment.set_code" placeholder="Set" /></div>

                            <div v-if="hasPrepWrap">
                                <div class="col-sm-3"><label for="account_code">Prep/Wrap Acct</label></div>
                                <div class="col-sm-3"><input class="form-control" type="text" name="pw_budget_code" v-model="assignment.pw_budget_code" ref="assignment.pw_budget_code" placeholder="Acct" /></div>
                                <div class="col-sm-3"><input class="form-control" type="text" name="pw_fringe_code" v-model="assignment.pw_fringe_code" ref="assignment.pw_fringe_code" placeholder="Fringe" /></div>
                                <div class="col-sm-3 timecard">
                                    <div class="col-sm-6"><input class="form-control" type="text" name="pw_free_field" v-model="assignment.pw_free_field" ref="assignment.pw_free_field" placeholder="FF1" /></div>
                                    <div class="col-sm-6"><input class="form-control" type="text" name="pw_free_field2" v-model="assignment.pw_free_field2" ref="assignment.pw_free_field2" placeholder="FF2" /></div>
                                </div>
                                <div class="clear"></div>
                                <div class="col-sm-3"><label for="account_code"></label></div>
                                <div class="col-sm-3"><input class="form-control" type="text" name="pw_series_code" v-model="assignment.pw_series_code" ref="assignment.pw_series_code" placeholder="Series" /></div>
                                <div class="col-sm-3"><input class="form-control" type="text" name="pw_location_code" v-model="assignment.pw_location_code" ref="assignment.pw_location_code" placeholder="Location" /></div>
                                <div class="col-sm-3"><input class="form-control" type="text" name="pw_set_code" v-model="assignment.pw_set_code" ref="assignment.pw_set_code" placeholder="Set" /></div>
                            </div>
                        </div>
                        <div class="col-sm-12 bold" v-if="isCanada && assignment.rate_unit_id != 1 && assignment.metadata && assignment.metadata.includeVacation === true">This rate includes the vacation payout.</div>
                        <div class="col-sm-12 bold" v-else-if="isCanada && assignment.rate_unit_id == 1 && assignment.metadata && assignment.metadata.includeVacation === true">This rate <u>does not</u> include the vacation payout.</div>
                        <!-- <div class="col-sm-12 bold red" v-if="scale > 0 && minWage.isBelowScale">Error: Rate should be at least scale ${{ scaleMessage }} (${{ scale }} per hour)<br /></div>
                        <div class="col-sm-12 bold" v-else-if="scale > 0">Scale for this position is ${{ scaleMessage }} (${{ scale }} per hour)<br /></div>
                        <div class="col-sm-12 bold red" v-show="minWage.isBelow">Error: Rate should be at least ${{ minWage.minimum }}<br />{{ minWage.name }}</div>
                        <div class="col-sm-12 bold red" v-show="assignment.rate_unit_id==9">Warning: Flat means you will pay only a 1 time payment for the flat amount specified. Do not use for typical payroll.</div> -->
                    </div>
                    <button class="btn btn-danger m-right-10" @click="openLink">Edit Start</button>
                </div>
            


                <div class="col-sm-6">
                    <div class="col-sm-3 text-left"><label class="lowprofile-label"></label></div>
                    <div class="col-sm-9">
                        <div class="col-sm-2"><label class="lowprofile-label">Amount</label></div>
                        <div class="col-sm-2"><label class="lowprofile-label">Unit&nbsp&nbsp&nbsp&nbsp</label></div>
                        <div class="col-sm-2"><label class="lowprofile-label">Cap&nbsp&nbsp&nbsp&nbsp</label></div>
                        <div class="col-sm-2"><label class="lowprofile-label">Acct/GL</label></div>
                        <div class="col-sm-2"><label class="lowprofile-label">FF1</label></div>
                        <div class="col-sm-2"><label class="lowprofile-label">FF2</label></div>
                    </div>
                    <div class="clear"></div>
                    <div v-if="employee.show_agent==1">
                        <div class="col-sm-3"><label for="agent_fee">Agent Fee</label></div>
                        <div class="col-sm-9 timecard">
                            <div class="col-sm-4">
                                <div class="input-group">
                                    <input class="form-control amount" type="text" name="agent_fee" v-model="assignment.agent_fee" ref="assignment.agent_fee" placeholder="% of Pay" readonly />
                                    <div class="input-group-addon">%</div>
                                </div>
                            </div>
                            <div class="col-sm-2"><input class="form-control" type="text" placeholder="N/A" tabindex="-1" readonly /></div>
                            <div v-if="enableProBooks" class="timecard">
                                <div class="col-sm-2">
                                    <autocomplete :key="iteration" :options="probooksAccounts" name="agent_code" v-model="assignment.agent_code" ref="assignment.agent_code" :display="'code'" :preselect="assignment.agent_code" @select="setGlCode('agent')" placeholder="GL Code" :default-show="true" :highlight="-1" :display-changes="true">
                                        <template v-slot:item="option">
                                            <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                        </template>
                                    </autocomplete>
                                </div>
                                <div class="col-sm-2 timecard">
                                    <autocomplete :key="iteration" :options="probooksFreeFields" name="agent_free_field" v-model="assignment.agent_free_field" ref="assignment.agent_free_field" :display="'code'" :preselect="assignment.agent_free_field" @select="setGlCode('agent_free_field')" placeholder="FF1" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                        <template v-slot:item="option">
                                            <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                        </template>
                                    </autocomplete>
                                </div>
                                <div class="col-sm-2 timecard">
                                    <autocomplete :key="iteration" :options="probooksFreeFields" name="agent_free_field2" v-model="assignment.agent_free_field2" ref="assignment.agent_free_field2" :display="'code'" :preselect="assignment.agent_free_field2" @select="setGlCode('agent_free_field2')" placeholder="FF2" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                        <template v-slot:item="option">
                                            <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                        </template>
                                    </autocomplete>
                                </div>
                            </div>
                            <div v-else class="timecard">
                                <div class="col-sm-2"><input class="form-control" type="text" name="agent_code" v-model="assignment.agent_code" ref="assignment.agent_code" placeholder="GL Code"/></div>
                                <div class="col-sm-2"><input class="form-control" type="text" name="agent_free_field" v-model="assignment.agent_free_field" ref="assignment.agent_free_field" placeholder="FF1"/></div>
                                <div class="col-sm-2"><input class="form-control" type="text" name="agent_free_field2" v-model="assignment.agent_free_field2" ref="assignment.agent_free_field2" placeholder="FF2"/></div>
                            </div>
                        </div>
                    </div>
                    <div class="clear"></div>

                    <div class="col-sm-3"><label for="kit_rental">Kit Rental NT</label></div>
                    <div class="col-sm-9 timecard">
                        <div class="col-sm-2"><input class="form-control amount" type="text" name="kit_rental" v-model="assignment.kit_rental" ref="assignment.kit_rental" placeholder="Amount" readonly /></div>
                        <div class="col-sm-2">
                            <input class="form-control" name="kit_rate_unit" v-model="assignment.kit_rate_unit" ref="assignment.kit_rate_unit" title="" required readonly />
                        </div>
                        <div class="col-sm-2"><input class="form-control" type="text" name="kr_cap" v-model="assignment.kr_cap" ref="assignment.kr_cap" placeholder="Cap" readonly /></div>
                        <div v-if="enableProBooks" class="timecard">
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksAccounts" name="kr_code" v-model="assignment.kr_code" ref="assignment.kr_code" :display="'code'" :preselect="assignment.kr_code" @select="setGlCode('kr')" placeholder="GL Code" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="kr_free_field" v-model="assignment.kr_free_field" ref="assignment.kr_free_field" :display="'code'" :preselect="assignment.kr_free_field" @select="setGlCode('kr_free_field')" placeholder="FF1" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="kr_free_field2" v-model="assignment.kr_free_field2" ref="assignment.kr_free_field2" :display="'code'" :preselect="assignment.kr_free_field2" @select="setGlCode('kr_FF2')" placeholder="FF2" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                        </div>
                        <div v-else class="timecard">
                            <div class="col-sm-2"><input class="form-control" type="text" name="kr_code" v-model="assignment.kr_code" ref="assignment.kr_code" placeholder="GL Code"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="kr_free_field" v-model="assignment.kr_free_field" ref="assignment.kr_free_field" placeholder="FF1"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="kr_free_field2" v-model="assignment.kr_free_field2" ref="assignment.kr_free_field2" placeholder="FF2"/></div>
                        </div>
                    </div>
                    <div class="clear"></div>

                    <div class="col-sm-3"><label for="kit_rental_tax">Kit Rental Tax</label></div>
                    <div class="col-sm-9 timecard">
                        <div class="col-sm-2"><input class="form-control amount" type="text" name="kit_rental_tax" v-model="assignment.kit_rental_tax" ref="assignment.kit_rental_tax" placeholder="Amount" readonly /></div>
                        <div class="col-sm-2">
                            <input class="form-control" name="kit_rate_unit" v-model="assignment.kit_rate_unit" ref="assignment.kit_rate_unit" readonly title="" required>
                        </div>
                        <div class="col-sm-2"><input class="form-control" type="text" name="krtax_cap" v-model="assignment.krtax_cap" ref="assignment.krtax_cap" placeholder="Cap" readonly /></div>
                        <div v-if="enableProBooks" class="timecard">
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksAccounts" name="krtax_code" v-model="assignment.krtax_code" ref="assignment.krtax_code" :display="'code'" :preselect="assignment.krtax_code" @select="setGlCode('krtax_code')" placeholder="GL Code" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="krtax_free_field" v-model="assignment.krtax_free_field" ref="assignment.krtax_free_field" :display="'code'" :preselect="assignment.krtax_free_field" @select="setGlCode('krtax_free_field')" placeholder="FF1" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="krtax_free_field2" v-model="assignment.krtax_free_field2" ref="assignment.krtax_free_field2" :display="'code'" :preselect="assignment.krtax_free_field2" @select="setGlCode('krtax_FF2')" placeholder="FF2" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                        </div>
                        <div v-else class="timecard">
                            <div class="col-sm-2"><input class="form-control" type="text" name="krtax_code" v-model="assignment.krtax_code" ref="assignment.krtax_code" placeholder="GL Code"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="krtax_free_field" v-model="assignment.krtax_free_field" ref="assignment.krtax_free_field" placeholder="FF1"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="krtax_free_field2" v-model="assignment.krtax_free_field2" ref="assignment.krtax_free_field2" placeholder="FF2"/></div>
                        </div>
                    </div>
                    <div class="clear"></div>

                    <div class="col-sm-3"><label for="phone_allow">Cell Allowance</label></div>
                    <div class="col-sm-9 timecard">
                        <div class="col-sm-2"><input class="form-control amount" type="text" name="phone_allow" v-model="assignment.phone_allow" ref="assignment.phone_allow" placeholder="Amount" readonly /></div>
                        <div class="col-sm-2">
                            <input class="form-control " name="phone_rate_unit" v-model="assignment.phone_rate_unit" ref="assignment.phone_rate_unit" readonly title="" required />
                        </div>
                        <div class="col-sm-2"><input class="form-control" type="text" name="phone_cap" v-model="assignment.phone_cap" ref="assignment.phone_cap" placeholder="Cap" readonly /></div>
                        <div v-if="enableProBooks" class="timecard">
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksAccounts" name="phone_allow_code" v-model="assignment.phone_allow_code" ref="assignment.phone_allow_code" :display="'code'" :preselect="assignment.phone_allow_code" @select="setGlCode('phone_allow_code')" placeholder="GL Code" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="phone_allow_free_field" v-model="assignment.phone_allow_free_field" ref="assignment.phone_allow_free_field" :display="'code'" :preselect="assignment.phone_allow_free_field" @select="setGlCode('phone_allow_free_field')" placeholder="FF1" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="phone_allow_free_field2" v-model="assignment.phone_allow_free_field2" ref="assignment.phone_allow_free_field2" :display="'code'" :preselect="assignment.phone_allow_free_field2" @select="setGlCode('phone_allow_free_field2')" placeholder="FF2" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                        </div>
                        <div v-else class="timecard">
                            <div class="col-sm-2"><input class="form-control" type="text" name="phone_allow_code" v-model="assignment.phone_allow_code" ref="assignment.phone_allow_code" placeholder="GL Code"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="phone_allow_free_field" v-model="assignment.phone_allow_free_field" ref="assignment.phone_allow_free_field" placeholder="FF1"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="phone_allow_free_field2" v-model="assignment.phone_allow_free_field2" ref="assignment.phone_allow_free_field2" placeholder="FF2"/></div>
                        </div>
                    </div>
                    <div class="clear"></div>

                    <div class="col-sm-3"><label for="per_diem">Per Diem NT</label></div>
                    <div class="col-sm-9 timecard">
                        <div class="col-sm-2"><input class="form-control amount" type="text" name="per_diem" v-model="assignment.per_diem" ref="assignment.per_diem" placeholder="Amount" readonly /></div>
                        <div class="col-sm-2">
                            <input class="form-control " name="pd_rate_unit" v-model="assignment.pd_rate_unit" ref="assignment.pd_rate_unit" readonly title="" required />
                        </div>
                        <div class="col-sm-2"><input class="form-control" type="text" placeholder="N/A" tabindex="-1" readonly/></div>
                        <div v-if="enableProBooks" class="timecard">
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksAccounts" name="pd_code" v-model="assignment.pd_code" ref="assignment.pd_code" :display="'code'" :preselect="assignment.pd_code" @select="setGlCode('pd_code')" placeholder="GL Code" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="pd_free_field" v-model="assignment.pd_free_field" ref="assignment.pd_free_field" :display="'code'" :preselect="assignment.pd_free_field" @select="setGlCode('pd_free_field')" placeholder="FF1" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="pd_free_field2" v-model="assignment.pd_free_field2" ref="assignment.pd_free_field2" :display="'code'" :preselect="assignment.pd_free_field2" @select="setGlCode('pd_free_field2')" placeholder="FF2" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                        </div>
                        <div v-else class="timecard">
                            <div class="col-sm-2"><input class="form-control" type="text" name="pd_code" v-model="assignment.pd_code" ref="assignment.pd_code" placeholder="GL Code"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="pd_free_field" v-model="assignment.pd_free_field" ref="assignment.pd_free_field" placeholder="FF1"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="pd_free_field2" v-model="assignment.pd_free_field2" ref="assignment.pd_free_field2" placeholder="FF2"/></div>
                        </div>
                    </div>
                    <div class="clear"></div>

                    <div class="col-sm-3"><label for="per_diem_taxable">Per Diem Taxable</label></div>
                    <div class="col-sm-9 timecard">
                        <div class="col-sm-2"><input class="form-control amount" type="text" name="per_diem_taxable" v-model="assignment.per_diem_taxable" ref="assignment.per_diem_taxable" placeholder="Amount" readonly /></div>
                        <div class="col-sm-2">
                            <input class="form-control " name="pd_rate_unit" v-model="assignment.pd_rate_unit" ref="assignment.pd_rate_unit" readonly title="" required/>
                        </div>
                        <div class="col-sm-2"><input class="form-control" type="text" placeholder="N/A" tabindex="-1" readonly/></div>
                        <div v-if="enableProBooks" class="timecard">
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksAccounts" name="pd_taxable_code" v-model="assignment.pd_taxable_code" ref="assignment.pd_taxable_code" :display="'code'" :preselect="assignment.pd_taxable_code" @select="setGlCode('pd_taxable_code')" placeholder="GL Code" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="pdtax_free_field" v-model="assignment.pdtax_free_field" ref="assignment.pdtax_free_field" :display="'code'" :preselect="assignment.pdtax_free_field" @select="setGlCode('pdtax_free_field')" placeholder="FF1" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="pdtax_free_field2" v-model="assignment.pdtax_free_field2" ref="assignment.pdtax_free_field2" :display="'code'" :preselect="assignment.pdtax_free_field2" @select="setGlCode('pdtax_free_field2')" placeholder="FF2" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                        </div>
                        <div v-else class="timecard">
                            <div class="col-sm-2"><input class="form-control" type="text" name="pd_taxable_code" v-model="assignment.pd_taxable_code" ref="assignment.pd_taxable_code" placeholder="GL Code"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="pdtax_free_field" v-model="assignment.pdtax_free_field" ref="assignment.pdtax_free_field" placeholder="FF1"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="pdtax_free_field2" v-model="assignment.pdtax_free_field2" ref="assignment.pdtax_free_field2" placeholder="FF2"/></div>
                        </div>
                    </div>
                    <div class="clear"></div>

                    <div class="col-sm-3"><label for="lodging">Lodging NT</label></div>
                    <div class="col-sm-9 timecard">
                        <div class="col-sm-2"><input class="form-control amount" type="text" name="lodging" v-model="assignment.lodging" ref="assignment.lodging" placeholder="Amount" readonly /></div>
                        <div class="col-sm-2">
                            <input class="form-control " name="lodging_rate_unit" v-model="assignment.lodging_rate_unit" ref="assignment.lodging_rate_unit" readonly title="" required />
                        </div>
                        <div class="col-sm-2"><input class="form-control" type="text" placeholder="N/A" tabindex="-1" readonly /></div>
                        <div v-if="enableProBooks" class="timecard">
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksAccounts" name="lodging_code" v-model="assignment.lodging_code" ref="assignment.lodging_code" :display="'code'" :preselect="assignment.lodging_code" @select="setGlCode('lodging_code')" placeholder="GL Code" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="lodging_free_field" v-model="assignment.lodging_free_field" ref="assignment.lodging_free_field" :display="'code'" :preselect="assignment.lodging_free_field" @select="setGlCode('lodging_free_field')" placeholder="FF1" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="lodging_free_field2" v-model="assignment.lodging_free_field2" ref="assignment.lodging_free_field2" :display="'code'" :preselect="assignment.lodging_free_field2" @select="setGlCode('lodging_free_field2')" placeholder="FF2" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                        </div>
                        <div v-else class="timecard">
                            <div class="col-sm-2"><input class="form-control" type="text" name="lodging_code" v-model="assignment.lodging_code" ref="assignment.lodging_code" placeholder="GL Code"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="lodging_free_field" v-model="assignment.lodging_free_field" ref="assignment.lodging_free_field" placeholder="FF1"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="lodging_free_field2" v-model="assignment.lodging_free_field2" ref="assignment.lodging_free_field2" placeholder="FF2"/></div>
                        </div>
                    </div>
                    <div class="clear"></div>

                    <div class="col-sm-3"><label for="lodging_tax">Lodging Taxable</label></div>
                    <div class="col-sm-9 timecard">
                        <div class="col-sm-2"><input class="form-control amount" type="text" name="lodging_tax" v-model="assignment.lodging_tax" ref="assignment.lodging_tax" placeholder="Amount" readonly /></div>
                        <div class="col-sm-2">
                            <input class="form-control " name="lodging_rate_unit" v-model="assignment.lodging_rate_unit" ref="assignment.lodging_rate_unit" readonly title="" required />
                        </div>
                        <div class="col-sm-2"><input class="form-control" type="text" placeholder="N/A" tabindex="-1" readonly /></div>
                        <div v-if="enableProBooks" class="timecard">
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksAccounts" name="lodging_tax_code" v-model="assignment.lodging_tax_code" ref="assignment.lodging_tax_code" :display="'code'" :preselect="assignment.lodging_tax_code" @select="setGlCode('lodging_tax_code')" placeholder="GL Code" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="lodging_tax_free_field" v-model="assignment.lodging_tax_free_field" ref="assignment.lodging_tax_free_field" :display="'code'" :preselect="assignment.lodging_tax_free_field" @select="setGlCode('lodging_tax_free_field')" placeholder="FF1" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="lodging_tax_free_field2" v-model="assignment.lodging_tax_free_field2" ref="assignment.lodging_tax_free_field2" :display="'code'" :preselect="assignment.lodging_tax_free_field2" @select="setGlCode('lodging_tax_free_field2')" placeholder="FF2" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                        </div>
                        <div v-else class="timecard">
                            <div class="col-sm-2"><input class="form-control" type="text" name="lodging_tax_code" v-model="assignment.lodging_tax_code" ref="assignment.lodging_tax_code" placeholder="GL Code"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="lodging_tax_free_field" v-model="assignment.lodging_tax_free_field" ref="assignment.lodging_tax_free_field" placeholder="FF1"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="lodging_tax_free_field2" v-model="assignment.lodging_tax_free_field2" ref="assignment.lodging_tax_free_field2" placeholder="FF2"/></div>
                        </div>
                    </div>
                    <div class="clear"></div>

                    <div class="col-sm-3"><label for="car_rental">Car Allowance</label></div>
                    <div class="col-sm-9 timecard">
                        <div class="col-sm-2"><input class="form-control amount" type="text" name="car_rental" v-model="assignment.car_rental" ref="assignment.car_rental" placeholder="Amount" readonly /></div>
                        <div class="col-sm-2">
                            <input class="form-control " name="car_rate_unit" v-model="assignment.car_rate_unit" ref="assignment.car_rate_unit" readonly title="" required />
                        </div>
                        <div class="col-sm-2"><input class="form-control" type="text" name="cr_cap" v-model="assignment.cr_cap" ref="assignment.cr_cap" placeholder="Cap" readonly /></div>
                        <div v-if="enableProBooks" class="timecard">
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksAccounts" name="cr_code" v-model="assignment.cr_code" ref="assignment.cr_code" :display="'code'" :preselect="assignment.cr_code" @select="setGlCode('cr_code')" placeholder="GL Code" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="cr_free_field" v-model="assignment.cr_free_field" ref="assignment.cr_free_field" :display="'code'" :preselect="assignment.cr_free_field" @select="setGlCode('cr_free_field')" placeholder="FF1" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                            <div class="col-sm-2">
                                <autocomplete :key="iteration" :options="probooksFreeFields" name="cr_free_field2" v-model="assignment.cr_free_field2" ref="assignment.cr_free_field2" :display="'code'" :preselect="assignment.cr_free_field2" @select="setGlCode('cr_free_field2')" placeholder="FF2" :min-keyword-length="0" :default-show="true" :highlight="-1" :display-changes="true">
                                    <template v-slot:item="option">
                                        <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                    </template>
                                </autocomplete>
                            </div>
                        </div>
                        <div v-else class="timecard">
                            <div class="col-sm-2"><input class="form-control" type="text" name="cr_code" v-model="assignment.cr_code" ref="assignment.cr_code" placeholder="GL Code"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="cr_free_field" v-model="assignment.cr_free_field" ref="assignment.cr_free_field" placeholder="FF1"/></div>
                            <div class="col-sm-2"><input class="form-control" type="text" name="cr_free_field2" v-model="assignment.cr_free_field2" ref="assignment.cr_free_field2" placeholder="FF2"/></div>
                        </div>
                    </div>
                    <div class="clear"></div>
                    <div class="col-sm-3"><label for="employer_comments">Comments/Other<br />Start Details</label></div>
                    <div class="col-sm-9">
                        <textarea class="form-control" v-model="assignment.employer_comments" ref="assignment.employer_comments" rows="3" name="employer_comments" placeholder="Employer Comments" readonly></textarea>
                    </div>
                    <div class="col-sm-3"><label for="recurring_comments">Recurring Note<sup>*</sup></label></div>
                    <div class="col-sm-9">
                        <textarea class="form-control" v-model="assignment.recurring_comments" ref="assignment.recurring_comments" rows="2" name="recurring_comments" placeholder="Recurring Comments"></textarea>
                    </div>
                    <div class="col-sm-12 left"><sup>*</sup> Will show on printed timecards</div>
                </div>
            </div>
        </div>
        <div class="col-sm-8 p-sides-10">
            <div class="m-top-10 panel panel-success" id="employeePanel">
                <div class="panel-heading centered">
                    <h4 style="margin: 0 auto; text-align: center;">Employee and Tax Information</h4>
                </div>
                <div class="panel-body">
                    <div class="hidden"><input class="form-control mainId" type="text" id="empId" name="empId" placeholder="Emp ID #" readonly value="" /></div>
                    <div class="col-sm-5">
                        <p class="bold italic">General Info</p>
                        <div class="clear"></div>
                        <!-- <div class="form-group"> -->
                            <div class="col-sm-4"><label for="citizenship">Citizenship</label></div>
                            <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="citizenship" v-model="employee.citizenship" ref="employee.citizenship" placeholder="Other Ethnicity" tabindex="-1" readonly /></div>
                            <div class="clear"></div>

                            <div v-if="isForeign">
                                <div class="col-sm-4">
                                    <label for="foreign_tin"><span v-if="isCanada">SIN*</span><span v-else-if="isForeign">Gov't ID*</span></label>
                                </div>
                                <div class="col-sm-8"><input autocomplete="force_off_rand_1234>" class="form-control" :class="[ errors.foreign_tin === true ? 'form-error' : '', changed.foreign_tin ? 'form-change' : '' ]" type="text" name="foreign_tin" v-model="employee.foreign_tin" @change="verifySSN" placeholder="" ref="employee.foreign_tin" :required="isCanada && !isSsnNeeded" readonly /></div>
                            </div>
                            <div v-if="isSsnNeeded">
                                <div class="col-sm-4"><label for="tin">SSN*</label></div>
                                <div class="col-sm-8"><input class="form-control" :class="[ errors.tin === true ? 'form-error' : '', changed.tin ? 'form-change' : '' ]" autocomplete="false" type="text" name="tin" v-model="employee.tin" placeholder="XXX-XX-XXXX" @change="verifySSN" ref="employee.tin" :required="isSsnNeeded" readonly /></div>
                            </div>
                            <div class="col-sm-4"><label for="birth_date">DOB*</label></div>
                            <div class="col-sm-8">
                                <input autocomplete="force_off_rand_1234" class="form-control" type="text" name="birth_date" @change="verifyBirthDate" v-model="localFormatBirthDate" :placeholder="format.toUpperCase()" ref="employee.birth_date" required readonly />
                            </div>
                            <div class="col-sm-4"><label for="last_name">Last*</label></div>
                            <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="last_name" v-model="employee.last_name" ref="employee.last_name" placeholder="" required readonly /></div>

                            <div class="col-sm-4"><label for="first_name">First*</label></div>
                            <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="first_name" v-model="employee.first_name" placeholder="" ref="employee.first_name" required readonly /></div>

                            <div class="col-sm-4"><label for="middle_name">Middle</label></div>
                            <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="middle_name" v-model="employee.middle_name" ref="employee.middle_name" placeholder="" readonly /></div>

                            <div class="col-sm-4"><label for="alt_name">Nickname</label></div>
                            <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="alt_name" v-model="employee.alt_name" ref="employee.alt_name" placeholder="" readonly /></div>

                            <div class="col-sm-4"><label for="mobile_phone">Cell</label></div>
                            <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="mobile_phone" v-model="employee.mobile_phone" ref="employee.mobile_phone" placeholder="(xxx) xxx-xxxx" readonly /></div>

                            <!-- <div class="col-sm-4"><label for="second_phone">Home Ph.</label></div>
                            <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="second_phone" v-model="employee.second_phone" ref="employee.second_phone" placeholder="(xxx) xxx-xxxx" readonly /></div> -->

                            <div v-if="!isCanada">
                                <div class="col-sm-4"><label for="gender">Gender</label></div>
                                <div class="col-sm-8">
                                    <input class="form-control" name="gender" v-model="employee.gender" ref="employee.gender" title="" readonly />
                                </div>

                                <div class="col-sm-4"><label for="marital_status_id">Marital</label></div>
                                <div class="col-sm-8">
                                    <input class="form-control" name="marital_status" v-model="employee.marital_status" ref="employee.marital_status" title="" :required="assignment.loanout_status != 1 && !isCanada && employee.w4_exempt != 1" readonly />
                                </div>
                                <div v-if="employee.citizenship_id==18 || employee.citizenship_id==19">
                                    <div class="col-sm-4"><label for="alien_no"></label></div>
                                    <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="alien_no" v-model="employee.alien_no" ref="employee.alien_no" placeholder="USCIS / Alien Registration Number" tabindex="-1" readonly /></div>
                                </div>

                                <div class="col-sm-4"><label for="ethnicity">Ethnicity</label></div>
                                <div class="col-sm-8">
                                    <input class="form-control" name="ethnicity" v-model="employee.ethnicity" ref="employee.ethnicity" title="" readonly />
                                </div>
                                <div v-if="employee.ethnicity_id==26">
                                    <div class="col-sm-4"><label for="ethnicity_other"></label></div>
                                    <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="ethnicity_other" v-model="employee.ethnicity_other" ref="employee.ethnicity_other" placeholder="Other Ethnicity" tabindex="-1" readonly /></div>
                                </div>
                            </div>
                        <!-- </div> -->
                    </div>
                    <div class="col-sm-7">
                        <p class="bold italic">Your Permanent Address</p>
                        <div class="clear"></div>
                        <div class="form-group">

                            <div class="col-sm-3"><label for="email">Email*</label></div>
                            <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="email" v-model="employee.email" placeholder="" ref="employee.email" required readonly /></div>

                            <div @change="permanentAddressChange">
                                <div class="col-sm-3"><label for="main_address1">Line 1</label></div>
                                <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="main_address1" v-model="employee.main_address1" ref="employee.main_address1" placeholder="" readonly /></div>
                                <div class="col-sm-3"><label for="main_address2"><span v-if="employee.main_state_id==66">City/{{ translate('state', { capFirst: true }) }}/Postal</span><span v-else>Line 2</span></label></div>
                                <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="main_address2" v-model="employee.main_address2" ref="employee.main_address2" placeholder="" readonly /></div>
                                <div class="col-sm-3"><label for="main_city"><span v-if="employee.main_state_id==66">Country</span><span v-else>City</span></label></div>
                                <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="main_city" v-model="employee.main_city" placeholder="" ref="employee.main_city" required readonly /></div>
                                <div class="col-sm-3"><label for="main_state">{{ translate('state', { capFirst: true }) }}</label></div>
                                <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="main_state" v-model="employee.main_state" placeholder="" ref="employee.main_state" required readonly /></div>
                                <div v-if="employee.main_state_id!=66">
                                    <div class="col-sm-3"><label for="main_postal">Postal</label></div>
                                    <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="main_postal" v-model="employee.main_postal" placeholder="" ref="employee.main_postal" :required="employee.main_state_id!=66" readonly /></div>
                                    <div class="col-sm-3"><label for="main_country">Country</label></div>
                                    <div class="col-sm-9">
                                        <select class="form-control " name="main_country_id" v-model="employee.main_country_id" title="" ref="employee.main_country_id" required disabled>
                                            <option value="">Select One</option>
                                            <option value="1">United States</option>
                                            <option value="2">Canada</option>
                                            <option value="3">United Kingdom</option>
                                            <option value="999">Other</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div v-if="isUnion">
                                <div class="col-sm-3"><label for="union_dues_withholding_id">Union Dues</label></div>
                                <div class="col-sm-9">
                                    <select class="form-control" id="union_dues_withholding_id" name="union_dues_withholding_id" v-model="assignment.union_dues_withholding_id" title="" ref="assignment.union_dues_withholding_id" :required="assignment.union_local_id" readonly disabled>
                                        <option value="">Please Select One:</option>
                                        <option value="93">Please deduct Union Dues from my Pay{{ translate('check') }}</option>
                                        <option value="94">Do <u>NOT</u> deduct dues</option>
                                    </select>
                                </div>
                            </div>
                            <div v-if="isCanada && isUnion">
                                <div class="col-sm-3"><label for="union_member_id">Union Member</label></div>
                                <div class="col-sm-9">
                                    <select class="form-control" id="union_member_id" name="union_member_id" v-model="assignment.union_member_id" title="" ref="assignment.union_member_id" :required="isCanada && assignment.union_local_id" readonly disabled>
                                        <option value="">Please Select One:</option>
                                        <option value="251">Member</option>
                                        <option value="252">Permittee</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div class="clear"></div>

                        <div class="form-group">
                            <div v-if="isW4Needed && assignment.loanout_status == 0" class="m-top-10">
                                <p class="bold italic">IRS W-4 Withholding &nbsp; <span @click="w4Modal=true" class="glyphicon glyphicon-question-sign"></span>&nbsp;<span @click="w4Modal=true" class="smaller">Use Helper</span></p>
                                <div class="col-sm-4"><label for="w4_exempt"><span @click="w4Modal=true">Exempt?</span> </label></div>
                                <div class="col-sm-4">
                                    <select class="form-control" id="w4_exempt" name="w4_exempt" v-model="employee.w4_exempt" title="" ref="employee.w4_exempt" :required="assignment.loanout_status != 1" readonly disabled>
                                        <option value="0">No</option>
                                        <option value="1">Yes</option>
                                    </select>
                                </div>
                                <div class="col-sm-4"><input autocomplete="force_off_rand_1234" class="form-control" type="number" name="w4_job_count" v-model="employee.w4_job_count" placeholder="# Jobs" ref="employee.w4_job_count" readonly /></div>
                                <div class="col-sm-4"><label for="w4_dependent_adj"><span @click="w4Modal=true">Adjustments</span></label></div>
                                <div class="col-sm-4"><input autocomplete="force_off_rand_1234" class="form-control" type="number" name="w4_dependent_adj" v-model="employee.w4_dependent_adj" placeholder="Dependent $" ref="employee.w4_dependent_adj" readonly /></div>
                                <div class="col-sm-4"><input autocomplete="force_off_rand_1234" class="form-control" type="number" name="w4_other_income" v-model="employee.w4_other_income" placeholder="Other Income" ref="employee.w4_other_income" readonly /></div>
                                <div class="col-sm-4"></div>
                                <div class="col-sm-4"><input autocomplete="force_off_rand_1234" class="form-control" type="number" name="w4_deductions" v-model="employee.w4_deductions" placeholder="Deductions" ref="employee.w4_deductions" readonly /></div>
                                <div class="col-sm-4"><input autocomplete="force_off_rand_1234" class="form-control" type="number" name="w4_extra_wh" v-model="employee.w4_extra_wh" placeholder="Extra Withholding" ref="employee.w4_extra_wh" readonly /></div>
                                <div class="hidden"><input name="w4_children" v-model="employee.w4_children" ref="employee.w4_children" /></div>
                                <div class="hidden"><input name="w4_other_dependents" v-model="employee.w4_other_dependents" ref="employee.w4_other_dependents" /></div>
                            </div>
                        </div>
                    </div>
                    <div v-if="(employee.ethnicity_id==92 || employee.gender_id==89) && !isCanada">
                        <label for="disclose_text">Employee has declined to provide certain DEI information that is required for state reporting. Employers can determine this information by visual survey and/or other available information. No answer by employee or employer will result in the report output from Revolution indicating the answer to be “unknown”.</label>
                    </div>
                    <button class="btn btn-danger m-right-10" @click="openLink('employee')">Edit Employee Data</button>
                </div>
            </div>
        </div>
        <div class="col-sm-4 p-sides-10">
            <div class="m-top-10 panel panel-default" id="optional">
                <div class="panel-heading centered">
                    <h4 style="margin: 0 auto; text-align: center;">Optional Information</h4>
                </div>
                <div class="panel-body">
                    <div class="form-group">
                        <p class="bold italic">Mailing Address</p>
                        <div class="clear"></div>
                        <div @change="mailingAddressChange">
                            <div class="col-sm-3"><label for="mailing_address1">Line 1</label></div>
                            <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="mailing_address1" v-model="employee.mailing_address1" ref="employee.mailing_address1" placeholder="" readonly /></div>
                            <div class="col-sm-3"><label for="mailing_address2">Line 2</label></div>
                            <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="mailing_address2" v-model="employee.mailing_address2" ref="employee.mailing_address2" placeholder="" readonly /></div>
                            <div class="col-sm-3"><label for="mailing_city">City</label></div>
                            <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="mailing_city" v-model="employee.mailing_city" ref="employee.mailing_city" placeholder="" readonly /></div>
                            <div class="col-sm-3"><label for="mailing_state_id">{{ translate('state', { capFirst: true }) }}</label></div>
                            <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="mailing_city" v-model="employee.mailing_state" ref="employee.mailing_state" placeholder="" readonly /></div>
                            <div class="col-sm-3"><label for="mailing_postal">Postal</label></div>
                            <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="mailing_postal" v-model="employee.mailing_postal" ref="employee.mailing_postal" placeholder="" readonly /></div>
                            <div class="col-sm-3"><label for="mailing_country_id">Country</label></div>
                            <div class="col-sm-9">
                                <select class="form-control " name="mailing_country_id" v-model="employee.mailing_country_id" title="" ref="employee.mailing_country_id" readonly disabled>
                                    <option value="">Select One</option>
                                    <option value="1">United States</option>
                                    <option value="2">Canada</option>
                                    <option value="3">United Kingdom</option>
                                    <option value="999">Other</option>
                                </select>
                            </div>
                        </div>
                        <div class="clear"></div>
                        <div class="col-sm-3 m-top-10"><label for="employee_comments">Comments</label></div>
                        <div class="col-sm-9 m-top-10"><textarea class="form-control" name="employee_comments" v-model="assignment.employee_comments" ref="assignment.employee_comments" rows="6" id="employee_comments" placeholder="" readonly></textarea></div>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-sm-12 p-sides-10" v-show="assignment.loanout_status == 1">
            <div class="m-top-10 panel panel-info" id="loanoutForm">
                <div class="panel-heading centered">
                    <div class="centered">
                        <h4 style="margin: 0 auto; text-align: center;">Loanout Details</h4>
                    </div>
                </div>
                <div class="panel-body">
                    <div class="col-sm-6">
                        <div class="form-group">
                            <div class="col-sm-3"><label for="loanout_status">Loanout?</label></div>
                            <div class="col-sm-9">
                                <select class="form-control " name="loanout_status" v-model="assignment.loanout_status" ref="assignment.loanout_status" title="" readonly disabled>
                                    <option value="0">No, pay me as an Individual</option>
                                    <option value="1">Yes, pay my Company for this Job</option>
                                </select>
                            </div>

                            <div class="col-sm-3"><label for="loanout_name">Loanout Name</label></div>
                            <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="loanout_name" v-model="employee.loanout_name" placeholder="" ref="employee.loanout_name" :required="assignment.loanout_status == 1 ? true : false" readonly /></div>

                            <div class="col-sm-3"><label for="loanout_ein">Tax ID/FEIN</label></div>
                            <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="loanout_ein" v-model="employee.loanout_ein" placeholder="" ref="employee.loanout_name" :required="assignment.loanout_status == 1 ? true : false" readonly /></div>

                            <div class="col-sm-3"><label for="loanout_type_id">Entity Type</label></div>
                            <div class="col-sm-9">
                                <input class="form-control " name="loanout_type" v-model="employee.loanout_type" ref="employee.loanout_type" title="" readonly />
                            </div>

                            <div class="col-sm-3"><label for="loanout_exception_id">Tax Exceptions</label></div>
                            <div class="col-sm-9">
                                <input class="form-control " name="loanout_exception" v-model="employee.loanout_exception" ref="employee.loanout_exception" title="" readonly />
                            </div>

                            <div class="col-sm-3"><label for="loanout_owner">Owner</label></div>
                            <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="loanout_owner" v-model="employee.loanout_owner" placeholder="" ref="employee.loanout_owner" :required="assignment.loanout_status == 1 ? true : false" readonly /></div>

                            <div class="col-sm-3"><label for="loanout_owner_title">Owner Title</label></div>
                            <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="loanout_owner_title" v-model="employee.loanout_owner_title" placeholder="" ref="employee.loanout_owner_title" :required="assignment.loanout_status == 1 ? true : false" readonly /></div>
                        </div>
                    </div>
                    <div class="col-sm-6">
                        <div class="form-group">
                            <div @change="loanoutAddressChange">
                                <div class="col-sm-3"><label for="loanout_address1">Line 1</label></div>
                                <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="loanout_address1" v-model="employee.loanout_address1" ref="employee.loanout_address1" placeholder="" readonly /></div>

                                <div class="col-sm-3"><label for="loanout_address2">Line 2</label></div>
                                <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="loanout_address2" v-model="employee.loanout_address2" ref="employee.loanout_address2" placeholder="" readonly /></div>

                                <div class="col-sm-3"><label for="loanout_city">City</label></div>
                                <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="loanout_city" v-model="employee.loanout_city" placeholder="" ref="employee.loanout_city" :required="assignment.loanout_status == 1 ? true : false" readonly /></div>

                                <div class="col-sm-3"><label for="loanout_state">{{ translate('state', { capFirst: true }) }}</label></div>
                                <div class="col-sm-9">
                                    <input class="form-control " name="loanout_state" v-model="employee.loanout_state" title="" ref="employee.loanout_state" :required="assignment.loanout_status == 1 ? true : false" readonly />
                                </div>
                                <div class="col-sm-3"><label for="loanout_postal">Postal</label></div>
                                <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="loanout_postal" v-model="employee.loanout_postal" placeholder="" ref="employee.loanout_postal" :required="assignment.loanout_status == 1 ? true : false" readonly /></div>

                                <div class="col-sm-3"><label for="loanout_country">Country</label></div>
                                <div class="col-sm-9">
                                    <select class="form-control " name="loanout_country_id" v-model="employee.loanout_country_id" title="" ref="employee.loanout_country_id" required readonly disabled>
                                        <option value="">Select One</option>
                                        <option value="1">United States</option>
                                        <option value="2">Canada</option>
                                        <option value="3">United Kingdom</option>
                                        <option value="999">Other</option>
                                    </select>
                                </div>
                            </div>
                            <div class="col-sm-3"><label for="loanout_states_registered">{{ translate('state', { capFirst: true, pluralize: true }) }} Registered</label></div>
                            <div class="col-sm-9">
                                <input class="form-control" v-model="loanoutStatesRegistered" name="loanout_states_registered" readonly />
                            </div>
                        </div>
                    </div>
                    <div class="col-sm-12">
                        <p v-if="selectedEntityType && ( selectedEntityType.code == 'C_CORP' || selectedEntityType.code == 'S_CORP' )">To ensure streamline processing of {{selectedEntityType.name}}, please upload copies of Articles of Incorporation and W9.</p>
                        <p v-else-if="selectedEntityType && ( selectedEntityType.code == 'C_LLC' || selectedEntityType.code == 'S_LLC' || selectedEntityType.code == 'CC_LLC' )">To ensure streamline processing of {{selectedEntityType.name}}, please upload copies of IRS election Letter.</p>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-sm-12 p-sides-10" v-show="employee.show_agent == 1">
            <div class="m-top-10 panel panel-info" id="agentPanel">
                <div class="panel-heading centered">
                    <div class="right"><a @click="agentDocModal=true" data-toggle="tooltip" title="Upload Agent/Talent Paperwork" tabindex="-1"><span class="small glyphicon glyphicon-upload" aria-hidden="true"></span><span class="small">&nbsp;Upload Agent/Talent Documents</span></a></div>
                    <div class="centered">
                        <h4 style="margin: 0 auto; text-align: center;">Agency Details</h4>
                    </div>
                </div>
                <div class="panel-body">
                    <div class="col-sm-6">
                        <div class="form-group">
                            <div class="col-sm-3"><label for="special_type">Pay My Agent?</label></div>
                            <div class="col-sm-9">
                                <select class="form-control " name="special_type" v-model="employee.special_type" ref="employee.special_type" title="" readonly disabled>
                                    <option value="0">No, pay me as an Individual</option>
                                    <option value="1">Yes, pay my Agent</option>
                                </select>
                            </div>
<!-- 
                            <div class="col-sm-3"><label for="agent_name">Agent/Contact Name</label></div>
                            <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="agent_name" v-model="employee.special_type_properties.agent_name" placeholder="" ref="employee.special_type_properties.agent_name" :required="employee.special_type == 1 ? true : false" :readonly="readonly || employee.special_type == 0" /></div>

                            <div class="col-sm-3"><label for="agency_name">Agency Name</label></div>
                            <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="agency_name" v-model="employee.special_type_properties.agency_name" placeholder="" ref="employee.special_type_properties.agency_name" :required="employee.special_type == 1 ? true : false" :readonly="readonly || employee.special_type == 0" /></div>

                            <div class="col-sm-3"><label for="agency_phone">Phone</label></div>
                            <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="agency_phone" v-model="employee.special_type_properties.agency_phone" ref="employee.special_type_properties.agency_phone" placeholder="" :readonly="readonlyFinal || employee.special_type == 0" /></div>

                            <div class="col-sm-3"><label for="agent_email">Email</label></div>
                            <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="agent_email" v-model="employee.special_type_properties.agent_email" ref="employee.special_type_properties.agent_email" placeholder="" :readonly="readonlyFinal || employee.special_type == 0"></div> -->
                        </div>
                    </div>
                    <div class="col-sm-6">
                        <!-- <div class="form-group">
                            <div @change="agentAddressChange">
                                <div class="col-sm-3"><label for="agency_address1">Line 1</label></div>
                                <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="agency_address1" v-model="employee.special_type_properties.agency_address1" ref="employee.special_type_properties.agency_address1" placeholder="" :readonly="readonlyFinal || employee.special_type == 0" /></div>

                                <div class="col-sm-3"><label for="agency_address2">Line 2</label></div>
                                <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="agency_address2" v-model="employee.special_type_properties.agency_address2" ref="employee.special_type_properties.agency_address2" placeholder="" :readonly="readonlyFinal || employee.special_type == 0" /></div>

                                <div class="col-sm-3"><label for="agency_city">City</label></div>
                                <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="agency_city" v-model="employee.special_type_properties.agency_city" placeholder="" ref="employee.special_type_properties.agency_city" :required="employee.special_type == 1 ? true : false" :readonly="readonlyFinal || employee.special_type == 0" /></div>
                                <div class="col-sm-3"><label for="agency_state">{{ translate('state', { capFirst: true }) }}</label></div>
                                <div class="col-sm-9">
                                    <input class="form-control " name="agency_state_id" v-model="employee.special_type_properties.agency_state_id" title="" ref="employee.special_type_properties.agency_state_id" :required="employee.special_type == 1 ? true : false" readonly/>
                                </div>
                                <div class="col-sm-3"><label for="agency_postal">Postal</label></div>
                                <div class="col-sm-9"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="agency_postal" v-model="employee.special_type_properties.agency_postal" placeholder="" ref="employee.special_type_properties.agency_postal" :required="employee.special_type == 1 ? true : false" :readonly="readonlyFinal || employee.special_type == 0" /></div>
                            </div>
                        </div> -->
                    </div>
                </div>
            </div>
        </div>
        <div class="clear"></div>

        <div v-if="customFormEmployerFields" class="col-sm-12 p-sides-10">
            <div class="m-top-10 panel panel-primary" id="customPanel">
                <div class="panel-heading centered">
                    <h4 style="margin: 0 auto; text-align: center;">Other Deal Points</h4>
                </div>
                <div class="panel-body">
                    <div v-for="document in customEmployerForms" :key="document.id">
                        <div class="m-top-30" v-if="Array.isArray(document.fields) && document.fields.length > 0">
                            <h4 style="text-align: left" v-if="document.name">{{ document.name + ' Info' }}</h4>
                            <p style="text-align: left" v-if="document.description" v-html="document.description"></p>
                            <div v-for="custom in document.fields" :key="custom.id">
                                <div v-if="custom.role && custom.role === 'employer'">
                                    <div v-if="custom.description">
                                        <div class="left" style="text-align:left">
                                            <span v-html="custom.description"></span>
                                        </div>
                                        <div class="clear"></div>
                                    </div>
                                    <div v-if="custom.type === 'radio'" class="col-sm-12 left">
                                        <div class="radio left m-left-20">
                                            <label style="font-size: 1.0em; line-height:1.5em; text-align: left" :for="custom.field">
                                                <input type="radio"
                                                    v-model="customFormEmployerFields[custom.field].value"
                                                    :name="custom.field"
                                                    :value="custom.length"
                                                    :ref="'assignment.custom_form_employer_fields[' + custom.field + ']'"
                                                    :required="custom.required == 1 ? true : false"
                                                    readonly
                                                />
                                                {{ custom.name }}
                                            </label>
                                        </div>
                                    </div>
                                    <div v-else-if="custom.type === 'boolean'" class="col-sm-12 left">
                                        <p v-if="custom.description" class="left" v-html="custom.description"></p>
                                        <div class="clear"></div>
                                        <input type="checkbox" class="col-sm-2"
                                            v-model="customFormEmployerFields[custom.field].value"
                                            :name="custom.field"
                                            :value="custom.length"
                                            :ref="'assignment.custom_form_employer_fields[' + custom.field + ']'"
                                        />
                                        <label class="col-sm-10" style="font-size: 1.0em; line-height:1.5em; text-align: left" :for="custom.field" :ref="'description.custom_form_employer_fields.' + custom.field">{{ custom.name }}</label>
                                        <div class="clear"></div>
                                    </div>
                                    <div v-else class="col-sm-6 m-top-10">
                                        <div class="col-sm-5"><label class="right" style="line-height:1.5em; text-align: right" :for="custom.field">{{ custom.name }}</label></div>
                                        <div class="col-sm-7">
                                            <input
                                                v-model="customFormEmployerFields[custom.field].value"
                                                :type="custom.type"
                                                :name="custom.field"
                                                :id="custom.field"
                                                :class="custom.type === 'text' ? 'form-control' : 'left'"
                                                :ref="'assignment.custom_form_fields[' + custom.field + ']'"
                                                autocomplete="force_off_rand_1234"
                                                :required="custom.required == 1 ? true : false"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="clear"></div>
                            <hr style="color:#333333;"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="clear"></div>

        <div v-if="customFormFields" class="col-sm-12 p-sides-10">
            <div class="m-top-10 panel panel-primary" id="customPanel">
                <div class="panel-heading centered">
                    <h4 style="margin: 0 auto; text-align: center;">Employer-Specific Questions</h4>
                </div>
                <div class="panel-body">
                    <div v-for="document in customEmployeeForms" :key="document.id">
                        <div class="m-top-30" v-if="Array.isArray(document.fields) && document.fields.length > 0">
                            <h4 style="text-align: left" v-if="document.name">{{ document.name + ' Info' }}</h4>
                            <p style="text-align: left" v-if="document.description" v-html="document.description"></p>
                                <div v-for="custom in document.fields" :key="custom.id">
                                    <div v-if="!custom.role || custom.role !== 'employer'">
                                        <div v-if="custom.description">
                                            <div class="left" style="text-align:left">
                                                <span v-html="custom.description"></span>
                                            </div>
                                            <div class="clear"></div>
                                        </div>
                                        <div v-if="custom.type === 'radio'" class="col-sm-12 left">
                                            <div class="radio left m-left-20" readonly>
                                                <label style="font-size: 1.0em; line-height:1.5em; text-align: left" :for="custom.field">
                                                    <input type="radio"
                                                        v-model="customFormFields[custom.field].value"
                                                        :name="custom.field"
                                                        :value="custom.length"
                                                        :ref="'assignment.custom_form_fields[' + custom.field + ']'"
                                                        :required="custom.required == 1 ? true : false"
                                                        readonly
                                                    />
                                                    {{ custom.name }}
                                                </label>
                                            </div>
                                        </div>
                                        <div v-else class="col-sm-6 m-top-10">
                                            <div class="col-sm-5"><label class="right" style="line-height:1.5em; text-align: right" :for="custom.field">{{ custom.name }}</label></div>
                                            <div class="col-sm-7">
                                                <input
                                                    v-model="customFormFields[custom.field].value"
                                                    :type="custom.type"
                                                    :name="custom.field"
                                                    :id="custom.field"
                                                    :class="custom.type === 'text' ? 'form-control' : 'left'"
                                                    :ref="'assignment.custom_form_fields[' + custom.field + ']'"
                                                    autocomplete="force_off_rand_1234"
                                                    :required="custom.required == 1 ? true : false"
                                                    readonly
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            <div class="clear"></div>
                            <hr style="color:#333333;"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="clear"></div>

        <div class="col-sm-12 p-sides-10">
            <div class="m-top-10 panel panel-success" id="ddForm">
                <div class="panel-heading centered">
                    <h4 style="margin: 0 auto; text-align: center;">Direct Deposit</h4>
                </div>
                <div class="panel-body">
                    <div class="col-sm-3"><label for="dd_status">Do you wish to use Direct Deposit?</label></div>
                    <div class="col-sm-9">
                        <select class="form-control " name="dd_status" v-model="assignment.dd_status" @change="resetDd" ref="assignment.dd_status" title="" disabled readonly>
                            <option value="">Select an Option</option>
                            <option value="0">No</option>
                            <option value="1">Yes, Setup 1 Bank</option>
                            <option value="2">Yes, Setup 2 Banks</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>

        <div class="clear"></div>
        <div v-show="(assignment.kit_rental && assignment.kit_rental > 0) || (assignment.kit_rental_tax && assignment.kit_rental_tax > 0)" class="col-sm-12 p-sides-10">
            <div class="m-top-10 panel panel-warning" id="kitRental">
                <div class="panel-heading centered">
                    <h4 style="margin: 0 auto; text-align: center;">Kit Rental Details</h4>
                </div>
                <div class="panel-body">
                    <div v-if="kitInventory && kitInventory.value.length > 0" class="col-sm-12">
                        <div class="text-left m-top-10 m-bottom-20 centered">
                            <p>Offered Kit Rental : ${{ (kitInventory || {}).amount }} per {{assignment.kit_rate_unit}}</p>
                        </div>
                        <div class="clear"></div>
                        <div class="col-sm-1"><input class="label" />Qty</div>
                        <div class="col-sm-4"><input class="label" />Description</div>
                        <div class="col-sm-3"><input class="label" />Serial</div>
                        <div class="col-sm-2"><input class="label" />Item Value</div>
                        <div class="col-sm-2"><input class="label" />Rate</div>
                        <div v-for="line in kitInventory.value" :key="line.id">
                            <div class="col-sm-1"><input class="form-control" v-model=line.quantity readonly /></div>
                            <div class="col-sm-4"><input class="form-control" v-model=line.description readonly /></div>
                            <div class="col-sm-3"><input class="form-control" v-model=line.serial readonly /></div>
                            <div class="col-sm-2">
                                <div class="input-group">
                                    <div class="input-group-addon">$</div>
                                    <input class="form-control text-right" v-model=line.kitValue readonly />
                                </div>
                            </div>
                            <div class="col-sm-2">
                                <div class="input-group">
                                    <div class="input-group-addon">$</div>
                                    <input class="form-control text-right" v-model=line.rentalRate readonly />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="boxDocs.length > 0" class="left m-top-10">Kit Document(s) uploaded. See next section.</div>
                </div>
            </div>
        </div>
        <div class="clear"></div>

        <div class="col-sm-12 p-sides-10">
            <div class="m-top-10 panel panel-default" id="docPanel">
                <div class="panel-heading centered">
                    <h4 style="margin: 0 auto; text-align: center;">Uploaded Documents</h4>
                </div>
                <div class="panel-body">
                    <div v-if="filteredDocs.length === 0"><p>No Documents Uploaded</p></div>
                    <div v-else class="table-responsive m-tm-bottom-0">
                        <table class="m-bottom-0 table table-hover" id="filteredDocs">
                            <tr class="success">
                                <th>File</th>
                                <th>Type</th>
                                <th>Date Uploaded</th>
                                <th>Actions</th>
                            </tr>
                            <tr v-for="doc in filteredDocs" :key="doc.hash" >
                                <td>{{ doc.name }}</td>
                                <td>{{ doc.typeNew || doc.type }}</td>
                                <td>{{ doc.time_added }}</td>
                                <td>
                                    <a :href="'/viewer/view/asset/' + doc.hash" target="_download" data-toggle="tooltip" title="View" tabindex="-1"><span class="glyphicon glyphicon-eye-open" aria-hidden="true"></span></a>
                                    &nbsp;
                                    <a :href="'/viewer/download/asset/' + doc.hash" data-toggle="tooltip" title="Download" tabindex="-1"><span class="glyphicon glyphicon-download-alt" aria-hidden="true"></span></a>
                                </td>
                            </tr>
                        </table>
                    </div>
                    <!-- <Dropzone id="dropzone" class="m-top-20"
                        url="/restv2/assets/upload/starts"
                        :use-custom-dropzone-options="true"
                        :dropzone-options="dzoptions"
                        v-on:vdropzone-success="dzSuccess"
                    >
                        <input type="hidden" name="assignmentId" :value="assignment.id">
                        <input type="hidden" name="employeeId" :value="assignment.employee_id">
                    </Dropzone> -->
                </div>
            </div>
        </div>

        <div class="clear"></div>
        <div class="col-sm-12 p-sides-10">
            <div class="m-top-10 panel panel-danger" id="i9Panel">
                <div class="right"><a @click="i9DocModal=true" data-toggle="tooltip" title="Upload I-9 Paperwork" tabindex="-1"><span class="small glyphicon glyphicon-upload" aria-hidden="true"></span><span class="small">&nbsp;Upload I-9 Documents</span></a></div>
                <div class="panel-heading centered">
                    <h4 style="margin: 0 auto; text-align: center;">I-9 Verification<span v-if="assignment.loanout_status > 0"> (Optional for Loan-Outs)</span></h4>
                </div>
                <div class="panel-body">
                    <p class="lead">Please select one List A document <span class="underline">OR</span> both a List B <span class="underline">AND</span> a List C document. Also verify using <span class="underline italic">original documents presented by the employee</span>.</p>
                    <p v-show="everifyEligible" class="lead red">This location requires verification of employment eligibility via E-Verify.</p>
                    <div class="col-sm-4" style="background-color: #eeeefe; padding-left: 0px; padding-right: 30px;">
                        <p class="m-top-10 bold">LIST A<br /><span class="italic">Identity and Employment Authorization</span></p>
                        <div class="form-group">
                            <div class="col-sm-4"><label for="list_a_type">Document</label></div>
                            <div class="col-sm-8">
                                <select class="form-control" name="list_a_type_id" v-model="assignment.list_a_type_id" ref="assignment.list_a_type_id" :disabled="readonlyI9">
                                    <option value="">Select One</option>
                                    <option v-for="option in optionValuesI9ListA" :value="option.id">
                                        {{option.name}}
                                    </option>
                                </select>
                            </div>

                            <div class="col-sm-4"><label for="list_a_auth">Issuer</label></div>
                            <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="list_a_auth" v-model="assignment.list_a_auth" ref="assignment.list_a_auth" placeholder="" :readonly="readonlyI9"/></div>

                            <div class="col-sm-4"><label for="list_a_number">Doc Number</label></div>
                            <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" :class="[ errors.list_a_number === true ? 'form-error' : '', changed.list_a_number ? 'form-change' : '' ]" type="text" name="list_a_number" v-model="assignment.list_a_number" ref="assignment.list_a_number" placeholder="" :readonly="readonlyI9"/></div>

                            <div class="col-sm-4"><label for="list_a_exp">Expiration</label></div>
                            <div class="col-sm-8">
                                <date-field
                                    name="list_a_exp"
                                    @verify-date="verifyDate"
                                    v-model="assignment.list_a_exp"
                                    reference="assignment.list_a_exp"
                                    ref="assignment.list_a_exp"
                                    :format="format"
                                    :allowEmpty="true"
                                    :placeholder="format.toUpperCase()"
                                    :readonly="readonlyI9"
                                    :classes="[ 'form-control', errors.list_a_exp === true ? 'form-error' : '', changed.list_a_exp ? 'form-change' : '' ]"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="col-sm-4" style="border-left: 2px solid #AAAAAA; background-color: #feeeee;">
                        <p class="m-top-10 bold">LIST B<br /><span class="italic">Identity</span></p>
                        <div class="form-group">
                            <div class="col-sm-4"><label for="list_b_type">Document</label></div>
                            <div class="col-sm-8">
                                <select class="form-control" name="list_b_type_id" v-model="assignment.list_b_type_id" ref="assignment.list_b_type_id" :disabled="readonlyI9">
                                    <option value="">Select One</option>
                                    <option v-for="option in optionValuesI9ListB" :value="option.id">
                                        {{option.name}}
                                    </option>
                                </select>
                            </div>

                            <div class="col-sm-4"><label for="list_b_auth">Issuer</label></div>
                            <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="list_b_auth" v-model="assignment.list_b_auth" ref="assignment.list_b_auth" placeholder="" :readonly="readonlyI9" /></div>

                            <div class="col-sm-4"><label for="list_b_number">Doc Number</label></div>
                            <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" :class="[ errors.list_b_number === true ? 'form-error' : '', changed.list_b_number ? 'form-change' : '' ]" type="text" name="list_b_number" v-model="assignment.list_b_number" ref="assignment.list_b_number" placeholder="" :readonly="readonlyI9" /></div>

                            <div class="col-sm-4"><label for="list_b_exp">Expiration</label></div>
                            <div class="col-sm-4">
                                <date-field
                                    name="list_b_exp"
                                    @verify-date="verifyDate"
                                    v-model="assignment.list_b_exp"
                                    reference="assignment.list_b_exp"
                                    ref="assignment.list_b_exp"
                                    :format="format"
                                    :allowEmpty="true"
                                    :placeholder="format.toUpperCase()"
                                    :readonly="readonlyI9"
                                    :allow-set-na="true"
                                    :classes="[ 'form-control', errors.list_b_exp === true ? 'form-error' : '', changed.list_b_exp ? 'form-change' : '' ]"
                                />
                            </div>
                            <div class="hidden"><label for="list_b_exp_na">No Exp</label></div>
                            <div class="col-sm-4" v-if="!readonlyI9"><span class="btn btn-danger" id="list_b_exp_na" @click="setNoExpDate('list_b_exp')">No Exp Date</span></div>
                        </div>
                    </div>
                    <div class="col-sm-4" style="background-color: #feeeee;">
                        <p class="m-top-10 bold">LIST C<br /><span class="italic">Employment Authorization</span></p>
                        <div class="form-group">
                            <div class="col-sm-4"><label for="list_c_type">Document</label></div>
                            <div class="col-sm-8">
                                <select class="form-control" name="list_c_type_id" v-model="assignment.list_c_type_id" ref="assignment.list_c_type_id" :disabled="readonlyI9">
                                    <option value="">Select One</option>
                                    <option v-for="option in optionValuesI9ListC" :value="option.id">
                                        {{option.name}}
                                    </option>
                                </select>
                            </div>

                            <div class="col-sm-4"><label for="list_c_auth">Issuer</label></div>
                            <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" type="text" name="list_c_auth" v-model="assignment.list_c_auth" ref="assignment.list_c_auth" placeholder="" :readonly="readonlyI9" /></div>

                            <div class="col-sm-4"><label for="list_c_number">Doc Number</label></div>
                            <div class="col-sm-8"><input autocomplete="force_off_rand_1234" class="form-control" :class="[ errors.list_c_number === true ? 'form-error' : '', changed.list_c_number ? 'form-change' : '' ]" type="text" name="list_c_number" v-model="assignment.list_c_number" ref="assignment.list_c_number" placeholder="" :readonly="readonlyI9" /></div>

                            <div class="col-sm-4"><label for="list_c_exp">Expiration</label></div>
                            <div class="col-sm-4">
                                <date-field
                                    name="list_c_exp"
                                    @verify-date="verifyDate"
                                    v-model="assignment.list_c_exp"
                                    reference="assignment.list_c_exp"
                                    ref="assignment.list_c_exp"
                                    :format="format"
                                    :allowEmpty="true"
                                    :placeholder="format.toUpperCase()"
                                    :readonly="readonlyI9"
                                    :allow-set-na="true"
                                    :classes="[ 'form-control', errors.list_c_exp === true ? 'form-error' : '', changed.list_c_exp ? 'form-change' : '' ]"
                                />
                            </div>
                            <div class="hidden"><label for="list_c_exp_na">No Exp</label></div>
                            <div class="col-sm-4" v-if="!readonlyI9"><span class="btn btn-danger" id="list_c_exp_na" @click="setNoExpDate('list_c_exp')">No Exp Date</span></div>
                        </div>
                    </div>
                    <div class="left m-top-20" style="text-align: left;">
                        <div v-if="assignment.is_rehire">
                            <div>This employee is a rehire and will refer to the pre-existing I-9 unless a correction or update is made to the information.</div>
                        </div>
                        <div v-else-if="isI9Importable">
                            <div class="right"><a @click="importEeI9"><span class="btn btn-success bold shadow">Import I-9 Data</span></a>
                                <span class="bold italic">This employee entered in document information. Click to use this information or type it in yourself when you verify in person.</span>
                            </div>
                        </div>
                        <div v-else>
                            <div class="left bigger m-right-10"><input type="checkbox" name="i9_verified" v-model="i9_verified" ref="i9_verified" :disabled="readonlyI9" /></div>
                            <div class="right lead">I certify that the above information is correct because I have verified the information against the<br /><i>ORIGINAL</i> document(s) presented by the employee.</div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="clear"></div>
            <div class="m-top-10 panel panel-warning" id="job1">
                <div class="panel-heading centered">
                    <div class="panel-heading centered">
                        <div v-if="prevStart" @click="gotoPrevStart" class="left m-top-m5" >
                            <span id="chevron-arrow-left"></span> Previous
                        </div>
                        <div class="right m-top-m5">
                            <span class="p-right-5"><div v-if="loading" class="inline-loader" style="display:inline-block;vertical-align:middle;"></div></span>
                            <button class="btn m-top-m5 m-right-10" :class="modified ? 'btn-primary' : 'btn-default'" @click="save" :disabled="!modified">Save</button>
                            <button class="btn m-top-m5 m-right-10" :class="canApprove ? 'btn-success' : 'btn-default'" @click="approvalCheck" :disabled="!canApprove">Approve</button>
                            <span v-if="nextStart" @click="gotoNextStart">
                                Next<span id="chevron-arrow-right"></span>
                            </span>
                        </div>
                        <h4 style="margin: 0 auto; text-align: center; line-height: 0.5em;"><span class="bold">{{ `${employee.last_name}, ${employee.first_name}` + (assignment.loanout_status > 0 ? ` (${employee.loanout_name || 'TBD'})` : '') }}</span></h4>
                    </div>
                </div>
            </div>
            <div class="clear"></div>
        </div>
        <oasis-modal v-if="approveModal" style="z-index: 950" @close="approveModal=false">
            <template v-slot:header>Confirm e-Approval</template>
            <template v-slot:body>
                By signing below, I:<br />
                <strong>1)</strong> certify the information entered is true and correct to the best of your knowledge,<br />
                <strong>2)</strong> agree to pay an employee/contractor for work/services,<br />
                <strong>3)</strong> am completing forms that may be submitted to a government agency, and<br />
                <strong>4)</strong> have read the <a href="/about/communication" target="_blank">Electronic Signature Agreement</a> policy.<br />
                <div v-show="everifyEligible"><strong>4)</strong> agreeing to use E-Verify to verify employee's employment eligibility.<br /></div>
                <div class="margin-5" id="ack">
                    <span class="m-right-10"><input v-model="acknowledge[0]" value="1" type="checkbox" /></span><span>I agree to the terms above. Preview the <a @click="printStart">employment agreement</a>.</span>
                </div>
                <signature-pad signature-for="employer" :sig="employer.signature" @signed="updateSignature" :employer-first-name="employer.first_name" :employer-last-name="employer.last_name"></signature-pad>
            </template>
            <template v-slot:footer>
                <!-- <?php if (isset($finalApproveStart) && $finalApproveStart === true) : ?><button class="btn btn-warning m-right-10" @click="approveStartFinal">Send to Revolution</button><?php endif;?> -->
                <button class="btn btn-danger m-right-10" @click="approveModal=false">Cancel</button>
                <button class="btn btn-primary" @click="approveStart()">Yes, I Approve</button>
            </template>
        </oasis-modal>
        <Dialog ref="saveDialog" @close="dialogClose('saveDialog')">
            <!-- Main content here -->
            <p>Your data has changed.  Please choose to either Review, Discard, or Save your changes.</p>
        
            <!-- Custom buttons via named slot -->
            <template #footer-buttons>
                <button class="btn btn-default m-right-5" @click="dialogClose('saveDialog')">Review</button>
                <div v-if="true">
                    <button class="btn btn-danger m-right-5" @click="dialogDiscard('saveDialog')">Discard and Next</button>
                    <button class="btn btn-primary" @click="dialogSave('saveDialog')">Save and Next</button>
                </div>
                <div v-else>
                    <button class="btn btn-primary" @click="dialogSave('saveDialog', null)">Save</button>
                </div>
            </template>
        </Dialog>
        <Dialog ref="ignoreI9Dialog" @close="dialogClose('ignoreI9Dialog')">
            <!-- Main content here -->
            <div v-if="isI9Importable">
                <p>The Employee has entered I9 information.  Continue to <u>Approve</u> or <u>Go Back</u> and import/review the I9 fields?</p>
            </div>
            <div v-else-if="isI9Reviewable">
                <p>There is I9 information entered.  Continue to <u>Approve</u> or <u>Go Back</u> and review and certify the I9 information?</p>
            </div>
            <div v-else>
                <p>There is no I9 data.  Continue to <u>Approve</u>?</p>
            </div>
        
            <!-- Custom buttons via named slot -->
            <template #footer-buttons>
                <button class="btn btn-default m-right-5" @click="dialogClose('ignoreI9Dialog')">Go Back</button>
                <button class="btn btn-primary" @click="approveWithoutI9">Approve</button>
            </template>
        </Dialog>
    </div>
</template>

<style>
.m-top-m5 {
    margin-top: -5px
}
.reviewBox {
    position: absolute;
    left: 50%;
    transform: translate(-50%, 0%);
    top: 100px;
    z-index: 1000;
    border: 2px solid red;
    border-radius: 10px;
    box-shadow: 0px 0px 500px 100px #666;
    padding: 20px;
    background-color: white;
    width: 1500px;
    max-width: 90%;
}
.option {
    font-size: 1.1em;
    font-weight: bold;
    text-decoration: underline;
}
.info {
    font-weight: lighter;
}
.toast-success {
    position: fixed; top: 5%; left: 50%; transform: translate(-50%, -50%);
    background-color: #d6e9c6; z-index: 1001; border-radius: 10px; padding: 20px; max-width: 40%;
    font-size: 1.5em; font-weight: 1000; text-align: center;
}
.toast-error {
    position: fixed; top: 5%; left: 50%; transform: translate(-50%, -50%);
    background-color: #feeeee; z-index: 1001; border-radius: 10px; padding: 20px; max-width: 40%;
    font-size: 1.5em; font-weight: 1000; text-align: center;
}
</style>
